export type LocaleCode = 'en-US' | 'zh-CN';

export type I18nValues = Record<string, string | number>;

export type LocaleStorage = Pick<Storage, 'getItem' | 'setItem'>;

export const localeStorageKey = 'cc_lang';

const enUS = {
  app: {
    title: 'cc-connect',
    subtitle: 'Console v2',
    openHarness: 'Open migration fallback',
    logout: 'Logout',
    language: 'Language',
    navigation: 'Console navigation',
  },
  auth: {
    eyebrow: 'Console v2 auth',
    title: 'Sign in with a management token',
    description: 'Enter a management API token before opening Console v2 resources. The token is stored locally in this browser.',
    tokenLabel: 'Management token',
    tokenPlaceholder: 'Paste token',
    submit: 'Continue',
    required: 'Enter a management token to continue.',
    reauthTitle: 'Re-authentication required.',
  },
  nav: {
    overview: 'Overview',
    personas: 'Personas',
    prompts: 'Prompts',
    skills: 'Skills',
    tools: 'Tools/MCP',
    runtime: 'Runtime',
    system: 'System',
    settings: 'Settings',
  },
  routes: {
    home: 'Control plane',
    personas: 'Personas',
    prompts: 'Prompts',
    skills: 'Skills',
    tools: 'Tools/MCP',
    runtime: 'Runtime evidence',
    system: 'System',
    settings: 'Settings',
  },
  common: {
    create: 'Create',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    refresh: 'Refresh',
    refreshLiveData: 'Refresh live data',
    refreshing: 'Refreshing…',
    open: 'Open',
    close: 'Close',
    retry: 'Retry',
    viewDetails: 'View details',
  },
  states: {
    loading: 'Loading…',
    empty: 'No resources found',
    error: 'Something went wrong',
    unauthorized: 'Unauthorized',
    unavailable: 'Unavailable',
  },
  confirmations: {
    discardChanges: 'Discard unsaved changes?',
    deleteResource: 'Delete this resource?',
    unsavedChanges: 'You have unsaved changes.',
  },
  status: {
    available: 'Available',
    readOnly: 'Read-only',
    readOnlyPreview: 'Read-only preview',
    routing: 'Routing',
    resources: 'Resources',
    api: 'API',
    mode: 'Mode',
    typed: 'Typed',
    sharedClient: 'Shared client',
    hashRoutes: 'Hash routes',
    sharedComponents: 'Shared components',
    noMutationYet: 'No mutation yet',
  },
  resource: {
    statusAriaLabel: 'Resource status',
    surface: 'Surface',
    mode: 'Mode',
    api: 'API',
    evidence: 'Evidence',
    actions: 'Actions',
  },
  home: {
    eyebrow: 'Console',
    title: 'Control plane',
    description: 'Unified resource operations for personas, prompts, skills, MCP/tools, runtime evidence, and audit history.',
    resourcesTitle: 'Resources',
    resourcesDescription: 'Operate high-signal control plane resources from one shell.',
    targets: {
      personaWorkbench: 'Persona Workbench',
      promptControlPlane: 'Prompt Control Plane',
      skillLibrary: 'Skill Library',
      toolsMcp: 'Tools/MCP',
      runtimeEvidence: 'Runtime Evidence',
      auditHistory: 'Audit History',
      systemStatus: 'System Status',
      system: 'System',
      settings: 'Settings',
    },
    targetStatus: {
      livePartialEvidence: 'Live + partial evidence',
    },
  },
  modules: {
    promptControlPlane: {
      common: {
        unavailable: 'Unavailable',
        none: 'None',
        yes: 'Yes',
        no: 'No',
        defaultScope: 'default',
        knownChars: 'Known chars, redacted',
        charsRedacted: '{count} chars, redacted',
        locked: 'locked',
        configurable: 'configurable',
        enabled: 'Enabled',
        disabled: 'Disabled',
        builtin: 'builtin',
        managed: 'managed',
        loadingLiveData: 'Loading live management API data…',
        partialUnavailable: 'Partial live data unavailable.',
      },
      header: {
        title: 'Prompt Control Plane',
        description: 'Live Console v2 artifact management for prompt modules, builtin defaults, managed overrides, Markdown editing, and read-only usage evidence.',
        kindLabel: 'Kind',
        kindValue: 'Prompt modules',
        sourceLabel: 'Source',
        sourceValue: 'Management API',
        mutationPolicyLabel: 'Mutation policy',
        mutationPolicyValue: 'Evidence + confirmation',
        plaintextLabel: 'Plaintext prompts',
        plaintextValue: 'Visible; secrets redacted',
      },
      list: {
        refresh: 'Refresh live data',
        refreshing: 'Refreshing…',
        rawJson: 'View Config / Raw JSON',
        createPrompt: 'New Prompt',
        filterSummary: 'Showing {visible} of {total} prompt modules.',
        groupCount: '{visible} visible in group',
        legacyHarness: 'Migration fallback',
        modulesTitle: 'Prompt modules',
        modulesDescription: 'Core and builtin defaults are readable and immutable; managed overrides replace their builtin target in-place.',
        groups: {
          coreTitle: 'Core modules',
          coreDescription: 'Protected foundation modules such as core and context_engineering; read-only and override locked.',
          builtinTitle: 'Built-in modules',
          builtinDescription: 'Non-core builtin defaults; managed overrides stay with their builtin module.',
          consoleTitle: 'Console modules',
          consoleDescription: 'Managed prompt modules created in Console.',
          emptyTitle: 'No matching modules in this group.',
          emptyDescription: 'Adjust the search, type, or state filters to show more modules.',
        },
        assignmentsTitle: 'Assignment routes',
        assignmentsDescription: 'Open persona baseline or platform-scoped assignment views. Platform UI accepts real platforms only.',
        defaultAssignmentTitle: 'Default / Persona baseline',
        defaultAssignmentDescription: 'Persona prompt assignment state',
        linearAssignmentTitle: 'Linear platform assignment',
        linearAssignmentDescription: 'Inherited vs platform override evidence',
        larkAssignmentTitle: 'Lark platform assignment',
        larkAssignmentDescription: 'Real-platform override view',
        emptyTitle: 'No prompt modules loaded.',
        emptyDescription: 'Refresh live data or check management API availability.',
        tableModule: 'Module',
        tableClassification: 'Classification',
        tableSource: 'Source',
        tablePolicy: 'Policy',
        tableEvidence: 'Evidence',
        tableDetails: 'Info',
        tableActions: 'Actions',
        filters: {
          search: 'Search',
          searchPlaceholder: 'Name, type, source, evidence',
          classification: 'Type',
          state: 'State',
          allTypes: 'All types',
          allStates: 'All states',
          active: 'Active',
          inactive: 'Inactive',
          mutable: 'Mutable',
          readonly: 'Read-only',
          overrideAllowed: 'Override allowed',
        },
        actions: {
          open: 'Open',
          edit: 'Edit',
          override: 'Override',
        },
        mutable: 'Mutable',
        immutable: 'Immutable',
        overrideAllowed: 'Override allowed',
        overrideLocked: 'Override locked',
        sharedMetadata: 'Shared metadata',
        viewMetadata: 'View policy and evidence',
        missingEvidence: 'Missing evidence',
        draftStatus: 'Draft/unpublished',
        policyBlocked: 'Policy blocks override',
      },
      status: {
        modules: 'Modules',
        overrides: 'Overrides',
        mutable: 'Mutable',
        active: 'Active',
        assignment: 'Assignment',
        draft: 'Draft',
        platformUi: 'Platform UI',
        classification: 'Classification',
        published: 'Published',
        consumers: 'Consumers',
        preview: 'Preview',
        unpublished: 'Unpublished',
        available: 'Available',
        changed: 'Changed',
        clean: 'Clean',
        enabledCount: '{enabled}/{total} enabled',
        builtinManagedEvidence: '{builtin} builtin / {managed} managed',
        sameNameReplacement: 'Same-name builtin replacement',
        coreImmutableEvidence: 'Core and builtin defaults immutable',
        liveRows: 'Readable live API rows',
        draftAssignmentView: 'Draft assignment view',
        revision: 'revision {revision}',
        realPlatformsOnly: 'Real platforms only; *_MODE excluded',
        artifactVersions: 'Artifact manifest / versions',
        behindLatest: '{count} behind latest',
        promptRedacted: 'Plaintext prompt redacted',
      },
      detail: {
        eyebrow: 'Prompt module resource',
        description: 'Live prompt module detail with redacted preview, version history, consumer evidence, rollout, rollback, and guarded action states.',
        refreshEvidence: 'Refresh evidence',
        allPrompts: 'Prompt list',
        overview: 'Overview',
        evidence: 'Evidence',
        history: 'Versions',
        raw: 'Raw JSON',
        classificationTitle: 'Module classification',
        classificationDescription: 'Preserves core immutability, builtin default readability, and managed override replacement semantics.',
        redactedPreviewTitle: 'Markdown prompt preview',
        redactedPreviewDescription: 'Prompt plaintext is visible in Console v2 with secret-like values redacted before rendering.',
        actionEvidenceTitle: 'Version and lifecycle evidence',
        actionEvidenceDescription: 'Runtime-impacting and destructive artifact actions require preview plus an explicit confirmation checkbox.',
        versionHistoryTitle: 'Version history',
        versionHistoryDescription: 'Version specs are shown with secret-like values redacted.',
        rawDescription: 'Secret-like fields are redacted; prompt content remains visible in token-protected Console v2.',
        contentTitle: 'Prompt Markdown',
        contentDescription: 'Builtin default and managed override content are shown separately so replacement semantics are explicit.',
        editorTitle: 'Markdown-first editor',
        editorDescription: 'Edit prompt content structurally as Markdown. Draft JSON is available only in the drawer with a published diff.',
        redactedEditorLocked: 'Secret-like values were redacted for display, so Markdown editing and draft save are locked to avoid persisting placeholders. Reload a clean prompt or replace the full body from a trusted source.',
        draftJsonTitle: 'Draft JSON drawer',
        draftJsonDescription: 'Draft spec, published spec, and a line diff for the current Markdown draft.',
        draftJsonAction: 'Draft JSON / Diff',
        configJsonAction: 'View Config JSON',
        openEditorDrawer: 'Edit in drawer',
        viewMarkdownDrawer: 'View in drawer',
        openActionDrawer: 'Open action drawer',
        builtinDefaultTitle: 'Builtin default',
        managedOverrideTitle: 'Managed override',
        managedContentTitle: 'Managed prompt',
        usageTitle: 'Read-only usage evidence',
        usageDescription: 'Shows which Persona config scopes reference this prompt. Assignment changes stay in Persona config.',
        moduleId: 'Module ID',
        name: 'Name',
        status: 'Status',
        overrideTarget: 'Override target',
        versions: 'Versions',
        consumer: 'Consumer',
        platform: 'Platform',
        referenced: 'Referenced',
        runtime: 'Runtime',
        actionEvidence: 'Action evidence',
        noConsumersTitle: 'No consumer evidence.',
        noConsumersDescription: 'Publish-only actions can proceed only with version evidence; rollout and rollback are unavailable.',
        currentLatest: 'current v{current} · latest v{latest}',
        runtimeVersion: 'runtime v{version}',
        rollbackBuiltinSuffix: ' · rollback to builtin default',
        previewContent: 'Content',
        previewValidation: 'Validation',
        previewRevision: 'Revision',
        markdown: 'Markdown',
        priority: 'Priority',
        conditionsTitle: 'Conditions',
        platformConditions: 'Platform conditions',
        agentConditions: 'Persona/project conditions',
        markdownPreview: 'Markdown preview',
        editorDraftState: 'Draft state',
        editorDirty: 'Unsaved structured draft changes',
        editorClean: 'No unsaved structured draft changes',
        discardDraft: 'Discard draft edits',
        saveDraftPreview: 'Preview save draft',
        archivePreview: 'Preview archive',
        deletePreview: 'Preview delete',
        openPersonaConfig: 'Open Persona config',
        actions: {
          createOverride: 'Create managed override draft',
          createVersion: 'Create version from draft',
          publish: 'Publish selected version',
          rolloutAll: 'Rollout to all consumers',
          rolloutSelected: 'Selective rollout',
          rollback: 'Rollback consumers',
          rollbackBuiltinDefault: 'Rollback to builtin default',
          destructive: 'Destructive archive/delete',
          apply: 'Apply guarded action',
        },
      },
      assignments: {
        eyebrow: 'Prompt assignment resource',
        titleSuffix: 'prompts',
        description: 'Persona baseline and real-platform prompt assignments with explicit inherited vs platform override state.',
        refresh: 'Refresh assignments',
        refreshing: 'Refreshing…',
        platform: 'Platform',
        defaultPersona: 'Default / Persona',
        overview: 'Overview',
        modules: 'Modules',
        editPreview: 'Edit / Preview',
        rawJson: 'Raw JSON',
        persona: 'Persona',
        scope: 'Scope',
        source: 'Source',
        defaultScope: 'Default',
        platformScope: '{platform}',
        sourceTitle: 'Assignment source',
        sourceDescription: 'Default baseline remains isolated from Linear/Lark platform overrides.',
        sourceState: 'Source state',
        draftChanged: 'Draft changed',
        mutationTitle: 'Mutation state',
        mutationDescription: 'Assignment writes are exposed only through exact request previews and explicit confirmation.',
        openEditPreview: 'Open edit preview',
        modulesTitle: 'Assignment modules',
        modulesDescription: 'Rows identify inherited baseline entries and platform override entries explicitly.',
        edit: {
          title: 'Assignment edit preview',
          description: 'Every assignment mutation shows the exact PUT contract and requires explicit confirmation before saving draft changes.',
          reset: 'Reset to default',
          noPreviewTitle: 'No mutation preview selected.',
          noPreviewDescription: 'Select a module action to inspect the exact API request before confirmation.',
          saveDraft: 'Save assignment draft',
          enableAction: 'Enable',
          disableAction: 'Disable',
          actionTitle: '{action} {module}',
          previewEnable: 'Preview enable',
          previewDisable: 'Preview disable',
        },
        sourceStates: {
          baseline: 'Default',
          inheritedBaseline: 'Inherited from default',
          platformOverride: 'Platform override',
          platformAssignment: 'Platform assignment',
        },
        table: {
          module: 'Module',
          origin: 'Origin',
          enabled: 'Enabled',
          assignmentState: 'Assignment state',
          evidence: 'Evidence',
          emptyTitle: 'No assignment modules.',
          emptyDescription: 'The API returned an empty assignment view for this scope.',
          inheritedBaseline: 'Inherited baseline',
          platformOverride: 'Platform override',
          baseline: 'Baseline',
        },
      },
      actions: {
        applying: 'Applying guarded action…',
        applied: 'Action applied. Refreshing evidence…',
        unavailable: 'Action unavailable',
        overrideCreatedOpen: 'Override draft created. Opening managed override…',
        overrideCreatedRefresh: 'Override draft created. Refreshing evidence…',
        applyingAssignment: 'Applying guarded assignment mutation…',
        assignmentSaved: 'Assignment mutation saved to draft. Refreshing evidence…',
        confirmationRequired: 'Review and confirm the exact request preview before applying.',
        assignmentConfirmationRequired: 'Review and confirm the exact assignment preview before saving.',
        result: 'Action result:',
      },
      lifecycle: {
        createManaged: 'Create managed prompt module',
        createDescription: 'Create a prompt artifact and seed its first Markdown draft without assigning it to any Persona.',
        createEvidence: 'Creates artifact metadata, then saves a Markdown draft through the existing draft endpoint.',
        saveDraft: 'Save Markdown draft',
        saveDraftEvidence: 'Saves the structured PromptModuleSpec draft with CAS revision evidence.',
        archive: 'Archive prompt module',
        delete: 'Delete prompt module',
        destructivePreview: 'Destructive or runtime-impacting action requires this request preview and explicit confirmation.',
        name: 'Name',
        displayName: 'Display name',
        description: 'Description',
        markdown: 'Markdown',
        priority: 'Priority',
        previewCreate: 'Preview create',
        createAndSeed: 'Create + seed draft',
      },
      previews: {
        method: 'Method',
        endpoint: 'Endpoint',
        unavailableContractGap: 'Unavailable / contract gap:',
        selected: '{count} selected',
        selectedConsumers: '{count} consumers',
        candidateCurrent: '{persona} / {platform} · current v{current}',
      },
      confirmations: {
        reviewExactRequest: 'I reviewed the exact request preview and current evidence.',
        reviewAssignmentScope: 'I reviewed the exact assignment preview and platform scope.',
      },
      contractGaps: {
        createOverrideUnavailable: 'Only non-core builtin defaults with override_allowed can create overrides',
        coreImmutable: 'core is readable but immutable: it cannot be edited, overridden, disabled, archived, deleted, or assigned as a managed module',
        builtinDefaultReadOnly: 'Builtin defaults are code-defined and read-only here; create a managed override where policy allows it',
        promptUsageReadOnly: 'Prompt usage is read-only on this page; assignment changes belong in Persona config',
        createManagedMissingFields: 'Name and Markdown content are required before a managed prompt module can be created',
        createVersionPreviewMissing: 'Contract gap: no redacted draft preview is available for version creation',
        redactedEditorSourceUnsafe: 'Loaded editor content is already redacted; reload with a valid token before saving so [REDACTED] is not persisted',
        publishVersionMissing: 'Contract gap: no publishable version evidence is available',
        rolloutMissing: 'Contract gap: consumer/version evidence is unavailable for rollout_all',
        selectiveRolloutMissing: 'Contract gap: no behind-latest consumer evidence is available for selective rollout',
        rollbackMissing: 'Contract gap: no previous-version consumer evidence is available for rollback',
        rollbackBuiltinMissing: 'Contract gap: no builtin-default rollback consumer evidence is available',
        destructivePreviewMissing: 'Archive/delete is guarded: backend delete exists but no destructive preview DTO is exposed yet',
        assignmentRowMissing: 'Contract gap: assignment row {module} is unavailable in current API evidence',
        assignmentLocked: 'Assignment for {module} is locked by policy and cannot be changed',
        draftRevisionMissing: 'Contract gap: draft revision is unavailable, so assignment mutation cannot use CAS safely',
        assignmentActionMissing: 'Contract gap: no safe assignment action is available for {module}',
        platformRequired: 'Platform is required to reset platform override inheritance',
      },
      modelEvidence: {
        createOverride: 'Create a managed override draft seeded from the builtin default after confirmation',
        createVersion: 'Create a version from the current draft after reviewing redacted preview and validation',
        publish: 'Publish selected version v{version} after preview confirmation',
        noVersion: 'No version evidence available',
        rolloutAll: 'Rollout requires consumer table evidence and browser confirmation',
        selectiveRollout: 'Only behind-latest consumers are selected by default',
        rollback: 'Rollback uses previous referenced version from consumer evidence',
        rollbackBuiltin: 'Managed override consumer has previous version 0, so rollback restores builtin default',
        assignmentPreview: 'Assignment preview uses {evidence}; scope {scope}',
        resetPlatform: 'Clears {platform} platform_prompt_modules so this scope inherits Default / Persona baseline',
        coreReadable: 'Readable and immutable',
        builtinDefault: 'Code-defined builtin default',
        managedOverride: 'Managed override replaces builtin',
        managedArtifact: 'Managed artifact',
        runtimeBehind: 'Runtime behind latest',
        runtimeApplied: 'Runtime applied',
        runtimeVersion: 'Runtime v{version}',
        runtimeUnavailable: 'Runtime unavailable',
        valid: 'Valid',
        validationIssues: '{count} validation issue{suffix}',
        moduleAvailable: 'published v{version}',
        overrideArtifact: 'managed override {artifactId} v{version}',
        replacesBuiltin: 'replaces builtin {target}',
        codeDefault: 'code-defined builtin default',
        inheritedPromptModules: 'inherits baseline prompt_modules',
        platformStorage: 'platform override storage',
        storageEvidence: '{storage}, {count} module{suffix}, {inheritance}',
        direct: 'direct',
        inherited: 'inherited',
      },
    },
    personaList: {
      eyebrow: 'Personas',
      title: 'Persona list',
      description: 'Live management API view of registered personas. Open a row to continue into the canonical workbench route.',
      emptyTitle: 'No personas returned.',
      emptyDescription: 'Live Persona summaries appear after the management API returns registered personas.',
      loading: 'Loading personas…',
      unauthorizedTitle: 'Unauthorized',
      unauthorizedDescription: 'Sign in with a management token that can read Persona resources.',
      errorTitle: 'Unable to load personas',
      create: {
        action: 'Create Persona',
        title: 'Create Persona',
        description: 'Create a Persona resource first, then continue configuration in the Persona Workbench.',
        contractTitle: 'Backend contract',
        contractDescription: 'Uses POST /api/v1/persona_configs with the compatibility field project_name, then saves an initial draft through /draft. Prompt, Skill, and MCP bindings stay inside the Persona config center.',
        personaName: 'Persona ID / name',
        personaNamePlaceholder: 'wizard-blue',
        displayName: 'Display name',
        displayNamePlaceholder: 'Wizard Blue',
        descriptionLabel: 'Description',
        descriptionPlaceholder: 'What this Persona is responsible for',
        agentType: 'Agent type',
        provider: 'Provider (optional)',
        providerPlaceholder: 'openai',
        model: 'Model (optional)',
        modelPlaceholder: 'gpt-5.1-codex',
        guardrailTitle: 'Guardrails',
        guardrailDescription: 'Do not paste tokens or secrets. Sensitive reveal remains gated by management-token re-entry; raw JSON stays in Workbench drawers only.',
        close: 'Close',
        cancel: 'Cancel',
        submit: 'Create Persona',
        submitting: 'Creating…',
        success: 'Created {personaName}. Opening Workbench…',
        nameRequired: 'Enter a Persona ID/name before creating.',
        conflict: 'Duplicate Persona: {message}',
        error: 'Create failed: {message}',
      },
      lifecycle: {
        title: 'Lifecycle contract gaps',
        description: 'Clone, archive, and delete contract gaps are shown only after opening a row action, instead of occupying the default list page.',
        persona: 'Persona',
        openActions: 'Lifecycle actions',
        unavailableBadge: 'unavailable',
        unavailableAction: 'Unavailable',
        action: 'Action',
        state: 'Backend state',
        safety: 'Safety rule',
        clone: 'Clone Persona',
        cloneState: 'Contract gap: no dedicated clone endpoint is exposed.',
        cloneSafety: 'Blocked until a snapshot+create preview contract or backend clone route exists.',
        archive: 'Archive Persona',
        archiveState: 'Contract gap: Persona configs have no archive endpoint.',
        archiveSafety: 'Any future archive must show preview evidence and require explicit confirmation.',
        delete: 'Delete Persona',
        deleteState: 'Contract gap: Persona configs have no delete endpoint.',
        deleteSafety: 'Destructive delete remains unavailable until preview plus typed confirmation exists.',
      },
      actions: {
        more: 'More actions',
        moreFor: 'More actions for {personaName}',
        menuLabel: 'Persona actions for {personaName}',
        open: 'Open',
        openHelper: 'Open the canonical Persona Workbench route.',
        clone: 'Clone',
        archive: 'Archive',
        enable: 'Enable',
        disable: 'Disable',
        statusUnavailable: 'Contract gap: Persona config status mutation endpoint is not exposed.',
        archivedTerminal: 'Archived Personas are terminal until a backend restore contract exists.',
      },
      openWorkbench: 'Open Workbench',
      noPlatforms: 'No platforms',
      unavailable: 'Unavailable',
      partialData: 'Partial data',
      meta: {
        kind: 'Kind',
        kindValue: 'Persona summaries',
        source: 'Source',
        route: 'Route',
        mode: 'Mode',
        modeValue: 'Read-only navigation',
      },
      status: {
        personas: 'Personas',
        liveSessions: 'Live sessions',
        platforms: 'Platforms',
        partial: 'Partial',
        distinctPlatforms: 'Distinct platform names',
        partialEvidence: 'Unavailable data is explicit',
      },
      table: {
        name: 'Name',
        agent: 'Agent',
        platforms: 'Platforms',
        sessions: 'Sessions',
        skills: 'Skills',
        workDirectory: 'Work directory',
        actions: 'Actions',
        detail: 'Detail',
        sessionsValue: '{live} live / {total} total',
      },
      redirect: {
        eyebrow: 'Persona detail alias',
        title: 'Redirecting to Persona workbench',
        description: 'Direct Persona detail routes now resolve through the canonical workbench for {personaId}.',
        persona: 'Persona',
        canonicalRoute: 'Canonical route',
        mode: 'Mode',
        modeValue: 'Redirect alias',
        openWorkbench: 'Open workbench',
      },
    },
    personaWorkbench: {
      actions: {
        refreshLiveData: 'Refresh live data',
        refreshing: 'Refreshing…',
        promptAssignments: 'Prompt assignments',
        rawJson: 'View Config JSON',
        diff: 'Diff',
        snapshot: 'Snapshot',
        saveDraft: 'Save draft',
        savePublish: 'Save and publish',
        publishSelected: 'Publish selected version',
        moreVersions: 'More / View more',
        contractsDrawer: 'Contracts and preview',
        contractsBadge: 'drawer',
        legacyHarness: 'Migration fallback',
        resultLabel: 'Action result:',
        run: 'Run',
        unavailable: 'Unavailable',
        matrix: {
          title: 'Action contract matrix',
          description: 'Each control lists the backend contract, redacted preview state, and confirmation requirement.',
          action: 'Action',
          backend: 'Backend',
          previewState: 'Preview / unavailable state',
          confirmation: 'Confirmation',
          run: 'Run',
        },
        confirm: {
          reviewedEvidence: 'I reviewed current preview/evidence.',
          reviewPublishEvidence: 'I reviewed the selected version, draft diff, and runtime preview.',
        },
        messages: {
          noCurrentContract: 'Action unavailable: no current backend contract.',
          loading: 'Action unavailable while live data is refreshing.',
          noConfirmationContract: 'Action unavailable: no current backend confirmation/evidence contract.',
          applying: 'Applying {action}…',
          validationCompleted: 'Validation completed. Redacted result is available in the validation drawer.',
          previewRefreshed: 'Preview refreshed. Redacted result is available in the preview drawer.',
          draftSaved: 'Persona draft saved. Refreshing structured configuration evidence…',
          versionChangelog: 'Console version from Persona Workbench draft review.',
          noPublishableVersion: 'No publishable version returned by /versions.',
          applied: '{action} applied. Refreshing evidence…',
        },
        validate: {
          label: 'Validate draft',
          preview: 'Current draft revision {draftRevision} can be validated without rendering secret values.',
          unavailableNoDraft: 'No draft revision returned by /persona_configs/{id}.',
          confirmation: 'Not required; validation is read-only.',
          unavailableUntilDraft: 'Unavailable until a draft exists.',
        },
        preview: {
          label: 'Preview draft',
          preview: 'Preview contract loaded for draft revision {draftRevision} with redacted summary fields.',
          unavailableMissing: 'Preview payload has not loaded from /persona_configs/{id}/preview.',
          confirmation: 'Not required; preview is read-only.',
          unavailableUntilPreview: 'Unavailable until preview payload loads.',
        },
        createVersion: {
          label: 'Create version',
          preview: 'Creates an immutable version from current draft revision {draftRevision}.',
          confirmation: 'Required before creating a version.',
          unavailableUntilPreviewEvidence: 'Unavailable until preview evidence loads.',
        },
        publish: {
          label: 'Publish latest version',
          preview: 'Publishes latest immutable version v{targetVersion}; runtime effect must be reviewed after publish.',
          unavailableNoVersion: 'Unavailable because /versions returned no immutable version to publish.',
          confirmation: 'Required before publishing v{targetVersion}.',
          unavailableUntilVersion: 'Unavailable until a publishable version exists.',
        },
        rollback: {
          label: 'Rollback version',
          preview: 'Unavailable because no backend rollback preview contract exists.',
          confirmation: 'Unavailable; rollback requires preview plus explicit confirmation before runtime mutation.',
        },
        discard: {
          label: 'Discard draft',
          backendGap: 'Contract gap: no discard draft endpoint exists.',
          preview: 'Unavailable because no backend discard preview contract exists.',
        },
        reset: {
          label: 'Reset to published',
          backendGap: 'Contract gap: no reset-to-published endpoint exists.',
          preview: 'Unavailable because no backend reset preview contract exists.',
        },
        contractGapConfirmation: 'Unavailable; no safe backend mutation contract.',
      },
      drawers: {
        previewTitle: 'Persona config preview',
        validationTitle: 'Persona config validation',
        savePublishTitle: 'Save and publish',
        draftJsonTitle: 'Draft JSON drawer',
        draftJsonSectionTitle: 'Draft JSON',
        draftDiffTitle: 'Draft vs published diff',
        validationResultTitle: 'Validation result',
        unsavedDraftTitle: 'Unsaved draft changes detected',
        unsavedDraftDescription: 'Save the current draft as an immutable version before publishing if these changes are the intended release.',
        savedVersionTitle: 'Saved versions available',
        savedVersionDescription: 'Choose the saved Persona config version to publish. Versions are sorted newest first.',
        publishVersionTitle: 'Publish version',
        publishVersionDescription: 'Newest saved versions are shown first; use More to load older versions.',
        noVersionsTitle: 'No saved versions',
        noVersionsDescription: 'Save the current draft before publishing.',
        noDraftPayload: 'No draft payload returned.',
        noDiffPayload: 'No diff payload returned.',
        noPreviewPayload: 'No preview payload returned.',
        noValidationPayload: 'No validation result returned.',
        rows: {
          draftRevision: 'Draft revision',
          updatedAt: 'Updated at',
          updatedBy: 'Updated by',
          publishedVersion: 'Published version',
        },
      },
      configCenter: {
        runtimeTitle: 'Runtime configuration',
        runtimeDescription: 'Provider, model, thinking, token budget, and style now live on the Persona detail.',
        consoleTitle: 'Console controls',
        consoleDescription: 'Display mode, route debug, command guards, built-in prompt suppression, and session cleanup.',
        bindingsTitle: 'Prompt / Skill / MCP bindings',
        bindingsDescription: 'Persona is the composition center; resource pages show usage evidence only.',
        managePromptBindings: 'Manage prompt bindings',
        promptEvidence: 'Prompt evidence',
        hierarchyTitle: 'Default/platform hierarchy',
        hierarchyDescription: 'Default Persona settings stay visible alongside platform-specific overrides.',
        promptPlatform: 'Prompt platform',
        defaultPersona: 'Default Persona',
        diffView: 'Diff view',
        draftLifecycleTitle: 'Draft lifecycle summary',
        draftLifecycleDescription: 'Compact draft, published, and prompt-control metadata. Draft JSON and diff open on demand.',
        draftJsonDiffAction: 'Draft JSON + diff',
        moreRowsSummary: 'Additional scoped rows',
        jsonTitle: 'Explicit config JSON entries',
        jsonDescription: 'Use a single read-only config JSON view plus the draft JSON drawer when editing evidence is needed.',
        rows: {
          agentType: 'Agent type',
          runtimeProviderModel: 'Runtime provider/model',
          providerCatalog: 'Provider catalog',
          thinking: 'Thinking',
          tokenBudget: 'Token budget',
          personaStyle: 'Persona style',
          displayMode: 'Display mode',
          routeDebug: 'Route debug',
          platformDisplayOverrides: 'Platform display overrides',
          disabledCommands: 'Disabled commands',
          disabledBuiltinPrompts: 'Disabled built-in prompts',
          sessionCleanup: 'Session cleanup',
          skillBindings: 'Skill bindings',
          promptBindings: 'Prompt bindings',
          mcpBindings: 'MCP bindings',
          skillBindingControls: 'Skill binding controls',
          mcpBindingControls: 'MCP binding controls',
          platformBindings: 'Platform bindings',
          availableArtifacts: 'Available artifacts',
          defaultRuntime: 'Default runtime',
        },
      },
      runtimeEvidence: {
        title: 'Runtime divergence evidence',
        description: 'Published snapshot, live engine, restart, and drift evidence from Persona config status.',
        snapshotDetail: 'Snapshot detail',
        sessionDetail: 'Session detail',
        sessionJson: 'Session JSON',
        rows: {
          syncStatus: 'Sync status',
          runtimeLoaded: 'Runtime loaded',
          liveApplied: 'Live applied',
          restartRequired: 'Restart required',
          restartFields: 'Restart fields',
          runtimeEffect: 'Runtime effect',
          divergences: 'Divergences',
        },
      },
    },
    resourceManagement: {
      tabs: {
        overview: 'Overview',
        redactedJson: 'View Config JSON',
      },
      table: {
        evidence: 'Evidence',
        runtimeExposure: 'Runtime exposure',
        controls: 'Controls',
      },
      labels: {
        preset: 'Preset',
        source: 'Source',
        actions: 'Actions',
        evidenceState: 'Evidence state',
        controlState: 'Control state',
      },
      actions: {
        skillLibrary: 'Skill Library',
        loadedSkills: 'Loaded Skills',
        createSkill: 'Create Skill',
        editDraft: 'Edit Draft',
        lifecycleActions: 'Lifecycle Actions',
        openDetail: 'Open detail',
        mcpServers: 'MCP Servers',
        mcpPresets: 'MCP Presets',
        toolProxy: 'Tool Proxy',
        createNative: 'Create Native',
        createProxy: 'Create Proxy',
        importPreset: 'Import preset',
        openImportDrawer: 'Open import drawer',
        editConfig: 'Edit config',
        editConnection: 'Edit connection',
        promptPolicy: 'Prompt / policy',
        runtimeEvidence: 'Runtime evidence',
        detailTabs: 'Detail tabs',
        importDrawer: 'Import drawer',
        detail: 'Detail',
      },
      controls: {
        unavailableContractGap: 'Unavailable: contract gap',
        backendPreviewConfirmationGap: 'Unavailable: backend preview+confirmation contract gap',
        contractGapButton: 'Controls unavailable: contract gap',
        installContractGapMessage: 'Preset install/import is available with secret registry refs; lifecycle and destructive MCP actions remain disabled without preview+confirmation.',
        installPreviewConfirmationGap: 'Unavailable: install preview+confirmation contract gap',
        presetInstallAvailable: 'Preset install available: POST /api/v1/mcp_presets/{name}/install uses secret registry refs only',
        presetInstallAction: 'Install via preset import; no secret values displayed',
        mutationPreviewConfirmationGap: 'mutation preview+confirmation contract gap',
        enableDisableInstallPublishGap: 'enable/disable/install/publish preview+confirmation contract gap',
        personaBindingReadOnly: 'Persona binding read-only here',
      },
      evidence: {
        runtimeNotInferred: 'not inferred',
        runtimeNotInferredStatus: 'Not inferred',
        runtimeAssociationNotice: 'Persona/platform associations are configuration only; runtime exposure requires live skill/profile evidence.',
        loadedSkillsRuntimeOnly: 'Loaded Skills uses live persona detail evidence only; configured associations are not treated as runtime exposure.',
        requiresRuntimeProfileEvidence: 'Requires runtime/profile evidence, not persona/platform association',
        configuredArtifactRuntimeUnknown: 'Configured artifact; runtime exposure unknown until runtime/profile evidence exists',
        mcpDefinitionRuntimeUnknown: 'Definition artifact only; use Tool Proxy for runtime evidence',
      },
      presets: {
        summaryTitle: 'Preset summary',
        registryTitle: 'Preset registry',
        plannedDescription: 'MCP presets from the backend registry. GitHub-style import stays a fast creation path using secret registry refs only.',
        emptyTitle: 'No MCP presets returned.',
        missingDescription: 'The backend registry did not include this preset.',
      },
    },
    systemSettings: {
      tabs: { overview: 'Overview', json: 'View Config JSON' },
      nav: { settings: 'Settings', status: 'System Status', releaseOps: 'Release Ops', operationsDisabled: 'Operations disabled', legacyHarness: 'Migration fallback' },
      page: {
        eyebrow: 'Settings',
        loading: 'Loading live management API data…',
        partialTitle: 'Partial/read-only data',
        partialSummary: 'Some backend evidence is incomplete. Open diagnostics for contract gaps, DTO/debug details, and raw evidence.',
        serviceBuild: 'Service and build',
        serviceBuildDescription: 'Trustworthy fields from live status plus asset evidence.',
        configuredIntent: 'Configured intent',
        configuredIntentDescription: 'Configured intent is reported separately from live runtime state and deployment artifacts.',
        healthSummary: 'Health summary',
        healthSummaryDescription: 'API, storage, scheduler, daemon, platform adapter, relay, and secret registry health from explicit backend evidence.',
        deploymentFreshness: 'Deployment freshness',
        deploymentFreshnessDescription: 'Shows partial/stale evidence when commit or asset metadata is incomplete.',
        releaseDeploymentDescription: 'Evidence only; stale/partial states are not asserted as release facts.',
        authTokenMode: 'Auth and token mode',
        authTokenModeDescription: 'Token mode, persisted client token status, logout availability, and 401/403 behavior. Secret values are never rendered.',
        globalSettings: 'Global settings only',
        globalSettingsDescription: 'Global Console/System settings stay here. Persona, Prompt, Skill, and MCP configuration belongs in the Persona config center.',
        openStructuredEditor: 'Edit global settings',
        globalSettingsEditorTitle: 'Structured global settings draft',
        globalSettingsEditorDescription: 'Edit only global System settings fields with structured controls. Status, diagnostics, deployment freshness, runtime evidence, and sensitive reveal are not saved here.',
        globalSettingsStructuredBoundary: 'This draft is scoped to System global settings only. Persona, Prompt, Skill, and MCP configuration remains in its own area, and no token or secret value is rendered.',
        saveDraft: 'Save draft',
        discardDraft: 'Discard',
        openDraftJsonDiff: 'Draft JSON + diff',
        noUnsavedDraft: 'No unsaved draft',
        unsavedDraft: 'Unsaved draft',
        structuredDraft: 'Structured draft',
        prefillSource: 'Prefill source',
        saveDraftLocalMessage: 'Structured draft saved for this console session; backend persistence remains separate from diagnostics, sensitive reveal, and destructive actions.',
        draftSaved: 'Draft saved',
        globalSettingsDraftJsonTitle: 'System global settings Draft JSON + diff',
        backToStructuredEdit: 'Back to structured edit',
        globalSettingsDraftDiffTitle: 'Draft vs current global settings',
        globalSettingsEmptyDiff: 'No draft changes compared with current global settings.',
        managementApiIntentHelper: 'Boolean global intent for the management API surface; live API reachability remains read-only evidence.',
        webConsoleIntentHelper: 'Boolean global intent for Console v2 assets; deployment freshness remains read-only evidence.',
        schedulerIntentHelper: 'Boolean global intent for scheduler availability; cron rows and daemon health stay diagnostic.',
        relayBridgeIntentHelper: 'Boolean global intent for relay bridge availability; relay health and secret registry counts stay read-only.',
        relayBridgeCheckbox: 'Include relay bridge in the global settings draft',
        generalSettings: 'General global settings',
        generalSettingsDescription: 'Safe config-file fields that apply globally across the service.',
        languageEnglish: 'English',
        languageChinese: 'Chinese',
        cronSilentCheckbox: 'Suppress cron start notification',
        displaySettings: 'Display controls',
        displaySettingsDescription: 'Message display and route-debug controls. Persona display overrides remain outside System.',
        displayModeDefault: 'Default',
        displayModeConcise: 'Concise',
        displayModeQuiet: 'Quiet',
        streamPreviewSettings: 'Stream preview controls',
        streamPreviewSettingsDescription: 'Safe preview cadence controls; platform adapter credentials remain separate.',
        streamPreviewDisabledPlatformsHelper: 'Comma-separated platform names, e.g. feishu, wecom.',
        limitsCleanupSettings: 'Rate limits and cleanup',
        limitsCleanupSettingsDescription: 'Global rate-limit and inactive-session cleanup settings.',
        sessionCleanupCheckbox: 'Enable inactive session cleanup',
        jsonRule: 'Global JSON rule',
        jsonRuleDescription: 'System keeps exactly one configuration JSON entry point; editing surfaces may use Draft JSON drawers with draft-vs-published diff.',
        sensitiveReveal: 'Sensitive reveal guard',
        sensitiveRevealDescription: 'Reveal is disabled unless the backend requires token re-entry for every view and records redacted audit evidence.',
        sensitiveRevealGuard: 'Sensitive reveal token re-entry guard',
        sensitiveRevealDrawerTitle: 'Sensitive reveal token re-entry',
        sensitiveRevealEntryDescription: 'Secret reveal is available only from this drawer and still requires token re-entry before any value can be viewed.',
        reentryPlaceholder: 'Token re-entry required by policy',
        revealDisabled: 'Reveal disabled: contract gap',
        auditEvidence: 'Audit evidence',
        actionCenter: 'Explicit actions',
        actionCenterDescription: 'Diagnostics and sensitive reveal stay off the default page until an operator opens the appropriate drawer.',
        diagnosticsDetails: 'Diagnostics details',
        diagnosticsDescription: 'Detailed backend evidence, DTO/debug rows, and redacted diagnostic JSON are available on demand.',
        openDiagnostics: 'Diagnostics',
        openSensitiveReveal: 'Reveal drawer',
        closeDrawer: 'Close',
        runtimeStorage: 'Runtime storage',
        runtimeStorageDescription: 'Runtime counts are live; storage/daemon health needs backend aggregate evidence.',
        schedulerDaemon: 'Scheduler and daemon',
        schedulerDaemonDescription: 'Cron rows are read-only; daemon status is partial unless backend reports it.',
        platformAdapters: 'Platform adapters',
        platformAdaptersDescription: 'Connected/configured platform evidence from status and persona list DTOs.',
        platformFallback: 'No live platform evidence returned',
        relaySecrets: 'Relay and secret registry',
        relaySecretsDescription: 'Relay bindings and secret registry counts only; secret material is never returned.',
        legacyBridge: 'Legacy bridge',
        legacyBridgeDescription: 'Legacy entrypoint is retired; Console v2 is the only official frontend.',
        cutoverChecklist: 'Cutover checklist',
        cutoverChecklistDescription: 'Release readiness checks are evidence-only; contract gaps stay explicit.',
        actionGuards: 'Operational action guards',
        actionGuardsDescription: 'No restart/reset/flush/rollback action is one-click or enabled without preview plus confirmation.',
        action: 'Action',
        check: 'Check',
        state: 'State',
        evidence: 'Evidence',
        guardReason: 'Guard reason',
        control: 'Control',
        backendDTO: 'Backend contract gaps',
        backendDTODescription: 'Required to make build/deployment/auth/storage/daemon/reveal/action facts authoritative.',
        diagnosticsDrawerTitle: 'Diagnostics details',
        diagnosticNotices: 'Diagnostic notices',
        diagnosticNoticesDescription: 'Partial evidence, backend contract gaps, and DTO requirements stay inside this drawer.',
        diagnosticEvidence: 'Runtime diagnostic evidence',
        diagnosticEvidenceDescription: 'Read-only runtime, scheduler, platform, relay, and secret-registry details.',
        rawDiagnosticJson: 'Redacted diagnostic JSON',
        rawDiagnosticJsonDescription: 'Raw backend DTO/debug evidence stays in this drawer and is redacted before rendering.',
        releaseActionDrawerTitle: 'Operation preview and confirmation',
        previewStep: 'Preview',
        previewStepDescription: 'Dangerous operations must fetch a backend preview before execution. Missing preview contracts keep the operation unavailable.',
        typedConfirmation: 'Typed confirmation',
        typedConfirmationDescription: 'Execution requires an exact typed phrase after a successful preview.',
        confirmationPhrase: 'Type {phrase} to confirm',
        operationUnavailable: 'Operation unavailable',
        confirmOperation: 'Confirm operation',
        openPreview: 'Open preview',
        previewUnavailable: 'Preview unavailable',
      },
      header: {
        statusTitle: 'System Status',
        statusDescription: 'Live management-plane health, deployment evidence, runtime state, auth contract, relay, and secret registry visibility. Partial facts are labeled instead of inferred.',
        releaseTitle: 'Release Operations',
        releaseDescription: 'Read-only release and operational guard state. Destructive operations stay disabled without backend preview plus explicit confirmation.',
        settingsTitle: 'Settings',
        settingsDescription: 'Global settings, Console v2 auth status, sensitive reveal guard, and the single configuration JSON rule. Persona/Prompt/Skill/MCP configuration is intentionally excluded.',
        kind: 'Kind',
        statusKind: 'System status',
        releaseKind: 'Release operations',
        settingsKind: 'Settings',
        source: 'Source',
        mode: 'Mode',
        readOnly: 'Read-only',
        guardedReadOnly: 'Guarded read-only',
        structuredDraft: 'Structured draft',
      },
      status: { service: 'Service', deployment: 'Deployment', scheduler: 'Scheduler', actions: 'Actions' },
      labels: {
        managementApiIntent: 'Management API intent', webConsoleIntent: 'Web console intent', schedulerIntent: 'Scheduler intent', relayBridgeIntent: 'Relay bridge intent',
        version: 'Version', commit: 'Commit', builtAt: 'Built at', uptime: 'Uptime', webConsoleAssets: 'Web console assets', managementApi: 'Management API', freshness: 'Freshness', artifactState: 'Artifact state', assetPath: 'Asset path', assetCommit: 'Asset commit', assetFingerprint: 'Asset fingerprint', targetCommit: 'Target commit', evidenceSource: 'Evidence source', backendAssetEvidence: 'Backend asset evidence',
        managementToken: 'Management token', serviceTokens: 'Service tokens', tokenMode: 'Token mode', persistedToken: 'Persisted token status', tokenStorage: 'Token storage key', logout: 'Logout', httpAuthErrors: '401/403 handling', secretRegistryEntries: 'Secret registry entries', resolvedSecretRefs: 'Resolved secret refs', missingSecretRefs: 'Missing secret refs', unsupportedSecretRefs: 'Unsupported secret refs', secretValues: 'Secret values',
        globalScope: 'Settings scope', language: 'Language', logLevel: 'Log level', cronSilent: 'Cron silent mode', idleTimeoutMins: 'Idle timeout minutes', displayMode: 'Display mode', displayThinkingMaxLen: 'Thinking max length', displayToolMaxLen: 'Tool max length', displayRouteDebug: 'Route debug', streamPreview: 'Stream preview', streamPreviewEnabled: 'Stream preview enabled', streamPreviewIntervalMs: 'Preview interval ms', streamPreviewMinDeltaChars: 'Preview min delta chars', streamPreviewMaxChars: 'Preview max chars', streamPreviewDisabledPlatforms: 'Disabled preview platforms', rateLimit: 'Rate limit', rateLimitEnabled: 'Rate limit enabled', rateLimitMaxMessages: 'Rate limit max messages', rateLimitWindowSecs: 'Rate limit window seconds', sessionCleanup: 'Session cleanup', sessionCleanupEnabled: 'Session cleanup enabled', sessionCleanupInactiveDays: 'Cleanup inactive days', sessionCleanupKeepLatest: 'Cleanup keep latest', prefillSource: 'Prefill source', draftPersistence: 'Draft persistence', configPath: 'Config path', personaConfigBoundary: 'Persona config boundary', viewConfigJsonEntrypoint: 'Configuration JSON entry point', draftJsonDrawer: 'Draft JSON drawer', draftPublishedDiff: 'Draft-vs-published diff', rawDebugTabs: 'Raw debug JSON tabs', previewContract: 'Preview contract',
        reenterToken: 'Re-enter management token', revealSupport: 'Reveal support', revealContract: 'Reveal contract', auditAction: 'Audit action', auditEndpoint: 'Audit endpoint', auditRows: 'Reveal audit rows', latestAuditEvidence: 'Latest reveal audit evidence', auditSecretValues: 'Audit secret values',
        personas: 'Personas', managementApiRuntime: 'Management API runtime', liveSessions: 'Live sessions', runtimeProfiles: 'Runtime profiles', runtimeStorageHealth: 'Runtime storage health', schedulerStatus: 'Scheduler status', totalJobs: 'Total jobs', enabledJobs: 'Enabled jobs', daemonStatus: 'Daemon status', platformAdapters: 'Platform adapters', relayHealth: 'Relay health', secretRegistryCounts: 'Secret registry counts',
        legacyHarness: 'Migration fallback', relayBindings: 'Relay bindings', legacyBridgeState: 'Legacy bridge state', platforms: 'Platforms',
      },
      states: {
        live: 'Live', partialData: 'Partial data', guarded: 'Guarded', reachable: 'Reachable', unavailable: 'Unavailable', available: 'Available', fresh: 'Fresh', unknown: 'Unknown', configured: 'Configured', notConfigured: 'Not configured', statusCountOnly: 'Status count only', neverRendered: 'Never rendered', preservedHarness: 'Preserved as migration fallback', retiredLegacy: 'Retired; use Console v2', relayReachable: 'Relay API reachable', needsBackendDTO: 'Needs backend aggregate DTO', partialModeUnavailable: 'Partial data: mode unavailable', partialStatusUnavailable: 'Partial data: status unavailable', present: 'Present', notPresent: 'Not present', unknownPresence: 'Unknown presence', configuredCount: '{count} configured', enabledLimit: 'Enabled · {count} messages / {seconds}s', enabledCleanup: 'Enabled · inactive {days}d · keep latest {keep}', disabled: 'Disabled', previewRequired: 'Preview required', noLivePlatformEvidence: 'No live platform evidence returned', contractGap: 'Contract gap', required: 'Required', notRequired: 'Not required', requiredByPolicy: 'Required by policy', clientClearsPersistedToken: 'Client clears persisted token and returns to login', globalOnly: 'Global settings only', personaConfigSeparated: 'Persona/Prompt/Skill/MCP config is managed outside System', singleEntrypoint: 'Exactly one redacted config JSON entry point', draftDrawerOnly: 'Allowed only as edit-mode drawer', diffRequiredWhenEditing: 'Required when Draft JSON is shown', notAllowed: 'Not allowed', noAuditEvidence: 'No reveal audit evidence returned',
      },
      actions: { restart: 'Restart service', reset: 'Reset runtime state', reload: 'Reload configuration', flush: 'Flush runtime state', rollback: 'Rollback deployment', unknown: 'Unknown action' },
      checklist: {
        buildVersion: 'Version and commit visible',
        frontendAssetFreshness: 'Frontend asset freshness evidence',
        authGuard: 'Console v2 token guard',
        legacyFrontendRetired: 'Legacy frontend retired',
        sensitiveRevealAudit: 'Sensitive reveal token re-entry and audit',
        releaseOperationPreview: 'High-risk operation preview and confirmation',
      },
      reasons: {
        guardUnavailable: 'Disabled: backend preview plus explicit confirmation is not available end to end.',
        previewRequired: 'Preview and explicit confirmation required before execution.',
        backendPreviewUnavailable: 'Disabled: backend preview unavailable.',
        previewConfirmationUnavailable: 'Preview + confirmation unavailable',
        backendAssetContractGap: 'Backend does not expose frontend asset commit/fingerprint from embedded assets.',
        sensitiveRevealContractGap: 'Backend reveal endpoint with token re-entry and redacted audit evidence is not implemented.',
      },
      evidence: {
        statusEndpointUnavailable: 'status endpoint unavailable',
        systemOverviewDeployment: 'GET /api/v1/system/overview deployment evidence',
        assetCommitMatchesExpectedTarget: 'asset commit matches expected target',
        assetCommitDiffersExpectedTarget: 'asset commit differs from expected target',
        assetCommitBuildMetadataUnavailable: 'asset commit/build metadata unavailable',
        webConsoleFalse: '/api/v1/status web_console=false',
        backendAggregateDTO: 'backend aggregate DTO',
        authGuardClient: 'Console v2 login wrapper persists token, logout clears it, and 401/403 forces re-authentication',
      },
      notices: {
        separator: ': ',
        detailSeparator: '; ',
        deploymentFreshnessPrefix: 'deployment freshness',
        deploymentFreshnessSuffix: 'backend aggregate DTO needed for authoritative build/version/commit state',
        systemOverviewGap: 'system overview DTO unavailable: GET /api/v1/system/overview is needed before remaining contract gaps can be separated from missing endpoint data',
        fallbackError: 'partial evidence unavailable',
      },
    }
},
} as const;

type StringLeaves<T> = {
  readonly [Key in keyof T]: T[Key] extends string ? string : StringLeaves<T[Key]>;
};

export type LocaleResource = StringLeaves<typeof enUS>;

type DotPaths<T, Prefix extends string = ''> = {
  [Key in keyof T & string]: T[Key] extends string
    ? `${Prefix}${Key}`
    : DotPaths<T[Key], `${Prefix}${Key}.`>;
}[keyof T & string];

export type I18nKey = DotPaths<LocaleResource>;

const zhCN: LocaleResource = {
  app: {
    title: 'cc-connect',
    subtitle: '控制台 v2',
    openHarness: '打开 Harness',
    logout: '退出登录',
    language: '语言',
    navigation: '控制台导航',
  },
  auth: {
    eyebrow: 'Console v2 认证',
    title: '使用管理令牌登录',
    description: '打开 Console v2 资源前，请输入管理 API 令牌。令牌会保存在当前浏览器本地。',
    tokenLabel: '管理令牌',
    tokenPlaceholder: '粘贴令牌',
    submit: '继续',
    required: '请输入管理令牌后继续。',
    reauthTitle: '需要重新认证。',
  },
  nav: {
    overview: '概览',
    personas: '角色',
    prompts: '提示词',
    skills: '技能',
    tools: '工具/MCP',
    runtime: '运行时',
    system: '系统',
    settings: '设置',
  },
  routes: {
    home: '控制平面',
    personas: '角色',
    prompts: '提示词',
    skills: '技能',
    tools: '工具/MCP',
    runtime: '运行时证据',
    system: '系统',
    settings: '设置',
  },
  common: {
    create: '创建',
    cancel: '取消',
    save: '保存',
    delete: '删除',
    refresh: '刷新',
    refreshLiveData: '刷新实时数据',
    refreshing: '刷新中…',
    open: '打开',
    close: '关闭',
    retry: '重试',
    viewDetails: '查看详情',
  },
  states: {
    loading: '加载中…',
    empty: '未找到资源',
    error: '出现错误',
    unauthorized: '未授权',
    unavailable: '不可用',
  },
  confirmations: {
    discardChanges: '放弃未保存的更改？',
    deleteResource: '删除此资源？',
    unsavedChanges: '你有未保存的更改。',
  },
  status: {
    available: '可用',
    readOnly: '只读',
    readOnlyPreview: '只读预览',
    routing: '路由',
    resources: '资源',
    api: 'API',
    mode: '模式',
    typed: '已类型化',
    sharedClient: '共享客户端',
    hashRoutes: 'Hash 路由',
    sharedComponents: '共享组件',
    noMutationYet: '尚无变更操作',
  },
  resource: {
    statusAriaLabel: '资源状态',
    surface: '界面',
    mode: '模式',
    api: 'API',
    evidence: '证据',
    actions: '操作',
  },
  home: {
    eyebrow: '控制台',
    title: '控制平面',
    description: '统一管理角色、提示词、技能、MCP/工具、运行时证据和审计历史等资源操作。',
    resourcesTitle: '资源',
    resourcesDescription: '在同一个 Shell 中操作高信号控制平面资源。',
    targets: {
      personaWorkbench: '角色工作台',
      promptControlPlane: '提示词控制平面',
      skillLibrary: '技能库',
      toolsMcp: '工具/MCP',
      runtimeEvidence: '运行时证据',
      auditHistory: '审计历史',
      systemStatus: '系统状态',
      system: '系统',
      settings: '设置',
    },
    targetStatus: {
      livePartialEvidence: '实时 + 部分证据',
    },
  },
  modules: {
    promptControlPlane: {
      common: {
        unavailable: '不可用',
        none: '无',
        yes: '是',
        no: '否',
        defaultScope: '默认',
        knownChars: '已知字符数，已脱敏',
        charsRedacted: '{count} 个字符，已脱敏',
        locked: '已锁定',
        configurable: '可配置',
        enabled: '已启用',
        disabled: '已禁用',
        builtin: '内置',
        managed: '托管',
        loadingLiveData: '正在加载实时 management API 数据…',
        partialUnavailable: '部分实时数据不可用。',
      },
      header: {
        title: 'Prompt 控制面',
        description: 'Prompt 模块、内置默认值、托管 override、Markdown 编辑与只读使用证据的 Console v2 工件管理界面。',
        kindLabel: '类型',
        kindValue: 'Prompt 模块',
        sourceLabel: '来源',
        sourceValue: '管理 API',
        mutationPolicyLabel: '变更策略',
        mutationPolicyValue: '证据 + 确认',
        plaintextLabel: 'Prompt 明文',
        plaintextValue: '可见；secret 已脱敏',
      },
      list: {
        refresh: '刷新实时数据',
        refreshing: '刷新中…',
        rawJson: '查看配置 / 原始 JSON',
        createPrompt: '新建 Prompt',
        filterSummary: '当前展示 {visible} / {total} 个 prompt modules。',
        groupCount: '此组显示 {visible} 个',
        legacyHarness: '迁移兜底',
        modulesTitle: 'Prompt 模块',
        modulesDescription: 'Core 和内置默认值可读且不可变；托管 override 会原位替换其内置目标。',
        groups: {
          coreTitle: '核心模块',
          coreDescription: 'core、context_engineering 等基础契约模块；只读且不允许 override。',
          builtinTitle: '内置模块',
          builtinDescription: '非 core 的 builtin 默认模块；托管 override 仍归在对应内置模块上下文。',
          consoleTitle: 'Console 新增模块',
          consoleDescription: '用户在 Console 创建的 managed prompt modules。',
          emptyTitle: '该分组没有匹配模块。',
          emptyDescription: '调整搜索、类型或状态筛选可查看更多模块。',
        },
        assignmentsTitle: 'Assignment 路由',
        assignmentsDescription: '打开 persona baseline 或平台级 assignment 视图。平台 UI 仅接受真实平台。',
        defaultAssignmentTitle: '默认 / Persona baseline',
        defaultAssignmentDescription: 'Persona prompt assignment 状态',
        linearAssignmentTitle: 'Linear 平台 assignment',
        linearAssignmentDescription: 'Inherited 与 platform override 证据',
        larkAssignmentTitle: 'Lark 平台 assignment',
        larkAssignmentDescription: '真实平台 override 视图',
        emptyTitle: '未加载 prompt module。',
        emptyDescription: '刷新实时数据或检查 management API 可用性。',
        tableModule: '模块',
        tableClassification: '分类',
        tableSource: '来源',
        tablePolicy: '策略',
        tableEvidence: '证据',
        tableDetails: '信息',
        tableActions: '操作',
        filters: {
          search: '搜索',
          searchPlaceholder: '名称、类型、来源、证据',
          classification: '类型',
          state: '状态',
          allTypes: '全部类型',
          allStates: '全部状态',
          active: '活跃',
          inactive: '非活跃',
          mutable: '可编辑',
          readonly: '只读',
          overrideAllowed: '允许 override',
        },
        actions: {
          open: '打开',
          edit: '编辑',
          override: '创建 override',
        },
        mutable: '可变',
        immutable: '不可变',
        overrideAllowed: '允许 override',
        overrideLocked: 'Override 锁定',
        sharedMetadata: '共享元数据',
        viewMetadata: '查看策略与证据',
        missingEvidence: '缺少证据',
        draftStatus: '草稿/未发布',
        policyBlocked: '策略阻止 override',
      },
      status: {
        modules: '模块',
        overrides: 'Overrides 数',
        mutable: '可变',
        active: '活跃',
        assignment: 'Assignment 状态',
        draft: '草稿',
        platformUi: '平台 UI',
        classification: '分类',
        published: '已发布',
        consumers: 'Consumers 数',
        preview: '预览',
        unpublished: '未发布',
        available: '可用',
        changed: '有变更',
        clean: '干净',
        enabledCount: '{enabled}/{total} 个启用',
        builtinManagedEvidence: '{builtin} 个内置 / {managed} 个托管',
        sameNameReplacement: '同名内置原位替换',
        coreImmutableEvidence: 'Core 与内置默认值不可变',
        liveRows: '可读实时 API 行',
        draftAssignmentView: '草稿 assignment 视图',
        revision: '修订 {revision}',
        realPlatformsOnly: '仅真实平台；排除 *_MODE',
        artifactVersions: '工件清单 / 版本',
        behindLatest: '{count} 个落后 latest',
        promptRedacted: 'Prompt 明文已脱敏',
      },
      detail: {
        eyebrow: 'Prompt module 资源',
        description: 'Console v2 中的 prompt artifact 管理详情，包含 Markdown 编辑、托管 override 和只读使用证据。',
        refreshEvidence: '刷新证据',
        allPrompts: 'Prompt 列表',
        overview: '概览',
        evidence: '证据',
        history: '版本',
        raw: '配置 / 原始 JSON',
        classificationTitle: 'Module 分类',
        classificationDescription: '保留 core 不可变、内置默认值可读、托管 override 原位替换语义。',
        redactedPreviewTitle: 'Markdown prompt 预览',
        redactedPreviewDescription: 'Console v2 可查看 prompt 明文，但渲染前会脱敏 secret-like 值。',
        actionEvidenceTitle: '版本与生命周期证据',
        actionEvidenceDescription: '影响运行时或破坏性 artifact 操作必须先预览并显式确认。',
        versionHistoryTitle: '版本历史',
        versionHistoryDescription: '版本 spec 会保留 prompt 内容并脱敏 secret-like 值。',
        rawDescription: 'Secret-like 字段会脱敏；prompt 内容在 token-protected Console v2 中可见。',
        contentTitle: 'Prompt Markdown',
        contentDescription: '分别展示 builtin default 与 managed override 内容，明确替换语义。',
        editorTitle: 'Markdown-first 编辑器',
        editorDescription: '以 Markdown 结构化编辑 prompt。Draft JSON 只在右侧 drawer 中显示，并包含与发布版本的 diff。',
        redactedEditorLocked: '检测到 secret-like 值已为展示脱敏，因此锁定 Markdown 编辑和 draft 保存，避免持久化占位符。请重新加载干净内容，或从可信来源替换完整正文。',
        draftJsonTitle: '草稿 JSON 抽屉',
        draftJsonDescription: '当前 draft spec、发布 spec 与行级 diff。',
        draftJsonAction: '草稿 JSON / Diff',
        configJsonAction: '查看配置 JSON',
        openEditorDrawer: '在抽屉中编辑',
        viewMarkdownDrawer: '在抽屉中查看',
        openActionDrawer: '打开操作抽屉',
        builtinDefaultTitle: '内置默认值',
        managedOverrideTitle: '托管 override',
        managedContentTitle: '托管 Prompt',
        usageTitle: '只读使用证据',
        usageDescription: '展示哪些 Persona config scope 正在引用该 prompt；assignment 变更留在 Persona config。',
        moduleId: '模块 ID',
        name: '名称',
        status: '状态',
        overrideTarget: 'Override 目标',
        versions: '版本',
        consumer: '消费方',
        platform: '平台',
        referenced: '引用版本',
        runtime: '运行时',
        actionEvidence: '操作证据',
        noConsumersTitle: '没有 consumer 证据。',
        noConsumersDescription: '只有具备版本证据时才能执行 publish-only；rollout 和 rollback 不可用。',
        currentLatest: '当前 v{current} · latest v{latest}',
        runtimeVersion: '运行时 v{version}',
        rollbackBuiltinSuffix: ' · rollback 到内置默认值',
        previewContent: '内容',
        previewValidation: '校验',
        previewRevision: '修订',
        markdown: 'Markdown',
        priority: '优先级',
        conditionsTitle: '条件',
        platformConditions: '平台条件',
        agentConditions: 'Persona/project 条件',
        markdownPreview: 'Markdown 预览',
        editorDraftState: 'Draft 状态',
        editorDirty: '存在未保存的结构化 draft 变更',
        editorClean: '没有未保存的结构化 draft 变更',
        discardDraft: '丢弃 draft 编辑',
        saveDraftPreview: '预览保存 draft',
        archivePreview: '预览 archive',
        deletePreview: '预览 delete',
        openPersonaConfig: '打开 Persona config',
        actions: {
          createOverride: '创建托管 override draft',
          createVersion: '从 draft 创建版本',
          publish: '发布所选版本',
          rolloutAll: 'Rollout 到所有 consumers',
          rolloutSelected: '选择性 rollout',
          rollback: '回滚 consumers',
          rollbackBuiltinDefault: '回滚到内置默认值',
          destructive: '破坏性 archive/delete',
          apply: '执行受保护操作',
        },
      },
      assignments: {
        eyebrow: 'Prompt assignment 资源',
        titleSuffix: '提示词',
        description: '带有明确 inherited 与 platform override 状态的 persona baseline 和真实平台 prompt assignments。',
        refresh: '刷新分配',
        refreshing: '刷新中…',
        platform: '平台',
        defaultPersona: '默认 / Persona',
        overview: '概览',
        modules: '模块',
        editPreview: '编辑 / 预览',
        rawJson: '原始 JSON',
        persona: 'Persona',
        scope: '作用域',
        source: '来源',
        defaultScope: 'Default',
        platformScope: '{platform}',
        sourceTitle: 'Assignment 来源',
        sourceDescription: 'Default baseline 与 Linear/Lark platform overrides 保持隔离。',
        sourceState: '来源状态',
        draftChanged: 'Draft 是否变更',
        mutationTitle: '变更状态',
        mutationDescription: 'Assignment 写入仅通过精确请求预览和显式确认暴露。',
        openEditPreview: '打开编辑预览',
        modulesTitle: 'Assignment 模块',
        modulesDescription: '行内明确标识 inherited baseline 条目和 platform override 条目。',
        edit: {
          title: 'Assignment 编辑预览',
          description: '每次 assignment 变更都会显示精确 PUT 合约，并要求显式确认后才保存 draft。',
          reset: 'Reset to default',
          noPreviewTitle: '尚未选择变更预览。',
          noPreviewDescription: '选择 module 操作以在确认前检查精确 API 请求。',
          saveDraft: '保存 assignment draft',
          enableAction: '启用',
          disableAction: '禁用',
          actionTitle: '{action} {module}',
          previewEnable: '预览启用',
          previewDisable: '预览禁用',
        },
        sourceStates: {
          baseline: 'Default',
          inheritedBaseline: 'Inherited from default',
          platformOverride: '平台 override',
          platformAssignment: '平台 assignment',
        },
        table: {
          module: '模块',
          origin: '来源',
          enabled: '启用状态',
          assignmentState: 'Assignment 状态',
          evidence: '证据',
          emptyTitle: '没有 assignment modules。',
          emptyDescription: 'API 对此作用域返回了空 assignment 视图。',
          inheritedBaseline: '继承 baseline',
          platformOverride: '平台 override',
          baseline: '基线',
        },
      },
      actions: {
        applying: '正在执行受保护操作…',
        applied: '操作已执行，正在刷新证据…',
        unavailable: '操作不可用',
        overrideCreatedOpen: 'Override draft 已创建，正在打开托管 override…',
        overrideCreatedRefresh: 'Override draft 已创建，正在刷新证据…',
        applyingAssignment: '正在执行受保护 assignment 变更…',
        assignmentSaved: 'Assignment 变更已保存到 draft，正在刷新证据…',
        confirmationRequired: '执行前必须检查并确认精确请求预览。',
        assignmentConfirmationRequired: '保存前必须检查并确认精确 assignment 预览。',
        result: '操作结果：',
      },
      lifecycle: {
        createManaged: '创建托管 Prompt 模块',
        createDescription: '创建 Prompt 工件并写入首个 Markdown 草稿；不会分配到任何 Persona。',
        createEvidence: '先创建工件 metadata，再通过已有草稿接口写入 Markdown 草稿。',
        saveDraft: '保存 Markdown draft',
        saveDraftEvidence: '使用 CAS revision 证据保存结构化 PromptModuleSpec draft。',
        archive: '归档 Prompt module',
        delete: '删除 Prompt module',
        destructivePreview: '破坏性或影响运行时的操作需要此请求预览和显式确认。',
        name: '名称',
        displayName: '显示名称',
        description: '描述',
        markdown: 'Markdown',
        priority: '优先级',
        previewCreate: '预览创建',
        createAndSeed: '创建并写入 draft',
      },
      previews: {
        method: '方法',
        endpoint: '接口',
        unavailableContractGap: '不可用 / 合约缺口：',
        selected: '已选择 {count} 个',
        selectedConsumers: '{count} 个 consumers',
        candidateCurrent: '{persona} / {platform} · 当前 v{current}',
      },
      confirmations: {
        reviewExactRequest: '我已检查精确请求预览和当前证据。',
        reviewAssignmentScope: '我已检查精确 assignment 预览和平台作用域。',
      },
      contractGaps: {
        createOverrideUnavailable: '只有允许 override 的非 core 内置默认值可以创建 override',
        coreImmutable: 'core 可读但不可变：不能编辑、override、disable、archive、delete，也不能作为普通 managed module assign',
        builtinDefaultReadOnly: 'Builtin default 由代码定义，此处只读；如策略允许请创建 managed override',
        promptUsageReadOnly: 'Prompt 页面上的使用证据只读；assignment 变更属于 Persona config',
        createManagedMissingFields: '创建托管 Prompt 模块前必须填写名称和 Markdown 内容',
        createVersionPreviewMissing: '合约缺口：缺少脱敏 draft 预览，无法创建版本',
        redactedEditorSourceUnsafe: '编辑器加载到的内容已经被脱敏；保存前请用有效 token 重新加载，避免持久化 [REDACTED]',
        publishVersionMissing: '合约缺口：缺少可发布版本证据',
        rolloutMissing: '合约缺口：rollout_all 缺少 consumer/version 证据',
        selectiveRolloutMissing: '合约缺口：selective rollout 缺少 behind-latest consumer 证据',
        rollbackMissing: '合约缺口：rollback 缺少 previous-version consumer 证据',
        rollbackBuiltinMissing: '合约缺口：缺少 rollback 到内置默认值的 consumer 证据',
        destructivePreviewMissing: 'Archive/delete 已受保护：后端 delete 存在，但没有暴露 destructive preview DTO',
        assignmentRowMissing: '合约缺口：当前 API 证据中缺少 assignment 行 {module}',
        assignmentLocked: '{module} assignment 被策略锁定，不能变更',
        draftRevisionMissing: '合约缺口：缺少 draft revision，assignment 变更无法安全使用 CAS',
        assignmentActionMissing: '合约缺口：{module} 没有可安全执行的 assignment 操作',
        platformRequired: '重置 platform override 继承时必须指定平台',
      },
      modelEvidence: {
        createOverride: '确认后创建从内置默认值种子的托管 override draft',
        createVersion: '检查脱敏预览和校验后，从当前 draft 创建版本',
        publish: '确认预览后发布所选版本 v{version}',
        noVersion: '没有可用版本证据',
        rolloutAll: 'Rollout 需要 consumer 表格证据和浏览器确认',
        selectiveRollout: '默认仅选择 behind-latest consumers',
        rollback: 'Rollback 使用 consumer 证据中的 previous referenced version',
        rollbackBuiltin: '托管 override consumer 的 previous version 为 0，因此 rollback 会恢复内置默认值',
        assignmentPreview: 'Assignment 预览使用 {evidence}；作用域 {scope}',
        resetPlatform: '清除 {platform} platform_prompt_modules，使此作用域继承 Default / Persona baseline',
        coreReadable: '可读且不可变',
        builtinDefault: '代码定义的内置默认值',
        managedOverride: '托管 override 替换内置模块',
        managedArtifact: '托管 artifact',
        runtimeBehind: 'Runtime 落后 latest',
        runtimeApplied: 'Runtime 已应用',
        runtimeVersion: '运行时 v{version}',
        runtimeUnavailable: 'Runtime 不可用',
        valid: '有效',
        validationIssues: '{count} 个校验问题',
        moduleAvailable: '已发布 v{version}',
        overrideArtifact: '托管 override {artifactId} v{version}',
        replacesBuiltin: '替换内置 {target}',
        codeDefault: '代码定义的内置默认值',
        inheritedPromptModules: '继承 baseline prompt_modules',
        platformStorage: 'platform override 存储',
        storageEvidence: '{storage}，{count} 个 module，{inheritance}',
        direct: '直接配置',
        inherited: '继承',
      },
    },
    personaList: {
      eyebrow: '角色',
      title: '角色列表',
      description: '注册角色的实时管理 API 视图。打开任一行可进入规范工作台路由。',
      emptyTitle: '未返回角色。',
      emptyDescription: '管理 API 返回已注册角色后，将显示实时角色摘要。',
      loading: '正在加载角色…',
      unauthorizedTitle: '未授权',
      unauthorizedDescription: '请使用可读取角色资源的管理令牌登录。',
      errorTitle: '无法加载角色',
      create: {
        action: '新建角色',
        title: '新建角色',
        description: '先创建角色资源，再进入角色工作台继续配置。',
        contractTitle: '后端契约',
        contractDescription: '使用 POST /api/v1/persona_configs，并沿用兼容字段 project_name；随后通过 /draft 写入初始草稿。Prompt、Skill、MCP 绑定仍集中在角色配置中心。',
        personaName: '角色 ID / 名称',
        personaNamePlaceholder: 'wizard-blue',
        displayName: '展示名称',
        displayNamePlaceholder: 'Wizard Blue',
        descriptionLabel: '描述',
        descriptionPlaceholder: '说明该角色负责什么',
        agentType: 'Agent 类型',
        provider: 'Provider（可选）',
        providerPlaceholder: 'openai',
        model: '模型（可选）',
        modelPlaceholder: 'gpt-5.1-codex',
        guardrailTitle: '安全边界',
        guardrailDescription: '不要粘贴 token 或 secret。敏感值 reveal 仍需要重新输入管理令牌；原始 JSON 只保留在工作台抽屉中。',
        close: '关闭',
        cancel: '取消',
        submit: '新建角色',
        submitting: '创建中…',
        success: '已创建 {personaName}，正在打开工作台…',
        nameRequired: '请先输入角色 ID/名称。',
        conflict: '角色重复：{message}',
        error: '创建失败：{message}',
      },
      lifecycle: {
        title: '生命周期契约缺口',
        description: '克隆、归档、删除的契约缺口只在打开行级操作后展示，不占据默认列表页。',
        persona: '角色',
        openActions: '生命周期操作',
        unavailableBadge: '不可用',
        unavailableAction: '不可用',
        action: '操作',
        state: '后端状态',
        safety: '安全规则',
        clone: '克隆角色',
        cloneState: '契约缺口：当前没有专用 clone endpoint。',
        cloneSafety: '需要 snapshot+create 预览契约或后端 clone 路由后才能启用。',
        archive: '归档角色',
        archiveState: '契约缺口：Persona config 没有 archive endpoint。',
        archiveSafety: '未来任何归档都必须展示预览证据并要求显式确认。',
        delete: '删除角色',
        deleteState: '契约缺口：Persona config 没有 delete endpoint。',
        deleteSafety: '破坏性删除在具备预览和 typed confirmation 前保持不可用。',
      },
      actions: {
        more: '更多操作',
        moreFor: '{personaName} 的更多操作',
        menuLabel: '{personaName} 的角色操作',
        open: '打开',
        openHelper: '打开规范角色工作台路由。',
        clone: '克隆',
        archive: '归档',
        enable: '启用',
        disable: '停用',
        statusUnavailable: '契约缺口：当前未暴露 Persona config 状态变更 endpoint。',
        archivedTerminal: '已归档角色为终态，需后端 restore 契约后才能恢复。',
      },
      openWorkbench: '打开工作台',
      noPlatforms: '无平台',
      unavailable: '不可用',
      partialData: '部分数据',
      meta: {
        kind: '类型',
        kindValue: '角色摘要',
        source: '来源',
        route: '路由',
        mode: '模式',
        modeValue: '只读导航',
      },
      status: {
        personas: '角色',
        liveSessions: '在线会话',
        platforms: '平台',
        partial: '部分',
        distinctPlatforms: '不同平台名称',
        partialEvidence: '不可用数据会明确标记',
      },
      table: {
        name: '名称',
        agent: 'Agent 类型',
        platforms: '平台',
        sessions: '会话',
        skills: '技能',
        workDirectory: '工作目录',
        actions: '操作',
        detail: '详情',
        sessionsValue: '{live} 在线 / {total} 总计',
      },
      redirect: {
        eyebrow: '角色详情别名',
        title: '正在跳转到角色工作台',
        description: '直接角色详情路由现在会通过 {personaId} 的规范工作台解析。',
        persona: '角色',
        canonicalRoute: '规范路由',
        mode: '模式',
        modeValue: '跳转别名',
        openWorkbench: '打开工作台',
      },
    },
    personaWorkbench: {
      actions: {
        refreshLiveData: '刷新实时数据',
        refreshing: '刷新中…',
        promptAssignments: '提示词分配',
        rawJson: '查看配置 JSON',
        diff: '差异',
        snapshot: '快照',
        saveDraft: '保存草稿',
        savePublish: '保存并发布',
        publishSelected: '发布所选版本',
        moreVersions: '查看更多',
        contractsDrawer: '契约与预览',
        contractsBadge: '抽屉',
        legacyHarness: '迁移兜底',
        resultLabel: '操作结果：',
        run: '执行',
        unavailable: '不可用',
        matrix: {
          title: '操作契约矩阵',
          description: '每个控件都列出后端契约、已脱敏的预览状态和确认要求。',
          action: '操作',
          backend: '后端',
          previewState: '预览 / 不可用状态',
          confirmation: '确认',
          run: '执行',
        },
        confirm: {
          reviewedEvidence: '我已检查当前预览/证据。',
          reviewPublishEvidence: '我已检查所选版本、草稿差异和运行时预览。',
        },
        messages: {
          noCurrentContract: '操作不可用：当前没有后端契约。',
          loading: '实时数据刷新中，操作不可用。',
          noConfirmationContract: '操作不可用：当前没有后端确认/证据契约。',
          applying: '正在执行 {action}…',
          validationCompleted: '校验已完成。已脱敏结果可在校验抽屉中查看。',
          previewRefreshed: '预览已刷新。已脱敏结果可在预览抽屉中查看。',
          draftSaved: '角色草稿已保存。正在刷新结构化配置证据…',
          versionChangelog: '通过角色工作台草稿审查创建控制台版本。',
          noPublishableVersion: '/versions 未返回可发布版本。',
          applied: '{action} 已执行。正在刷新证据…',
        },
        validate: {
          label: '校验草稿',
          preview: '当前草稿版本 {draftRevision} 可校验，且不会渲染密钥值。',
          unavailableNoDraft: '/persona_configs/{id} 未返回草稿版本。',
          confirmation: '无需确认；校验为只读操作。',
          unavailableUntilDraft: '草稿存在后才可用。',
        },
        preview: {
          label: '预览草稿',
          preview: '已为草稿版本 {draftRevision} 加载预览契约和脱敏摘要字段。',
          unavailableMissing: '尚未从 /persona_configs/{id}/preview 加载预览载荷。',
          confirmation: '无需确认；预览为只读操作。',
          unavailableUntilPreview: '预览载荷加载后才可用。',
        },
        createVersion: {
          label: '创建版本',
          preview: '从当前草稿版本 {draftRevision} 创建不可变版本。',
          confirmation: '创建版本前必须确认。',
          unavailableUntilPreviewEvidence: '预览证据加载后才可用。',
        },
        publish: {
          label: '发布最新版本',
          preview: '发布最新不可变版本 v{targetVersion}；发布后必须复核运行时影响。',
          unavailableNoVersion: '/versions 未返回可发布的不可变版本，因此不可用。',
          confirmation: '发布 v{targetVersion} 前必须确认。',
          unavailableUntilVersion: '存在可发布版本后才可用。',
        },
        rollback: {
          label: '回滚版本',
          preview: '不可用：后端尚无回滚预览契约。',
          confirmation: '不可用；回滚在变更运行时前必须具备预览和显式确认。',
        },
        discard: {
          label: '放弃草稿',
          backendGap: '契约缺口：不存在放弃草稿的后端端点。',
          preview: '不可用：不存在后端放弃草稿预览契约。',
        },
        reset: {
          label: '重置为已发布',
          backendGap: '契约缺口：不存在重置为已发布版本的后端端点。',
          preview: '不可用：不存在后端重置预览契约。',
        },
        contractGapConfirmation: '不可用；没有安全的后端变更契约。',
      },
      drawers: {
        previewTitle: '角色配置预览',
        validationTitle: '角色配置校验',
        savePublishTitle: '保存并发布',
        draftJsonTitle: '草稿 JSON 抽屉',
        draftJsonSectionTitle: '草稿 JSON',
        draftDiffTitle: '草稿与已发布差异',
        validationResultTitle: '校验结果',
        unsavedDraftTitle: '检测到未保存草稿变更',
        unsavedDraftDescription: '如果这些变更就是本次发布内容，请先将当前草稿保存为不可变版本。',
        savedVersionTitle: '已有已保存版本',
        savedVersionDescription: '选择要发布的角色配置版本。版本按新到旧排序。',
        publishVersionTitle: '发布版本',
        publishVersionDescription: '默认显示最新保存版本；使用 More 加载更早版本。',
        noVersionsTitle: '暂无已保存版本',
        noVersionsDescription: '发布前请先保存当前草稿。',
        noDraftPayload: '未返回草稿载荷。',
        noDiffPayload: '未返回差异载荷。',
        noPreviewPayload: '未返回预览载荷。',
        noValidationPayload: '未返回校验结果。',
        rows: {
          draftRevision: '草稿版本',
          updatedAt: '更新时间',
          updatedBy: '更新人',
          publishedVersion: '已发布版本',
        },
      },
      configCenter: {
        runtimeTitle: '运行时配置',
        runtimeDescription: 'Provider、model、thinking、token budget 和 style 现在都位于角色详情中。',
        consoleTitle: '控制台控制',
        consoleDescription: '显示模式、route debug、命令保护、内置提示词禁用和会话清理。',
        bindingsTitle: '提示词 / 技能 / MCP 绑定',
        bindingsDescription: '角色是组合中心；资源页只展示使用证据。',
        managePromptBindings: '管理提示词绑定',
        promptEvidence: '提示词证据',
        hierarchyTitle: '默认/平台层级',
        hierarchyDescription: '默认角色设置和平台级覆盖保持并列可见。',
        promptPlatform: '提示词平台',
        defaultPersona: '默认角色',
        diffView: '差异视图',
        draftLifecycleTitle: 'Draft lifecycle 摘要',
        draftLifecycleDescription: '仅展示紧凑草稿、发布和提示词控制元数据。草稿 JSON 与 diff 按需打开。',
        draftJsonDiffAction: '草稿 JSON + diff',
        moreRowsSummary: '更多作用域行',
        jsonTitle: '显式配置 JSON 入口',
        jsonDescription: '保留一个只读配置 JSON 视图，并在需要编辑证据时使用草稿 JSON 抽屉。',
        rows: {
          agentType: 'Agent 类型',
          runtimeProviderModel: '运行时提供商/模型',
          providerCatalog: '提供商目录',
          thinking: '思考级别',
          tokenBudget: 'Token 预算',
          personaStyle: '角色风格',
          displayMode: '显示模式',
          routeDebug: '路由调试',
          platformDisplayOverrides: '平台显示覆盖',
          disabledCommands: '禁用命令',
          disabledBuiltinPrompts: '禁用内置提示词',
          sessionCleanup: '会话清理',
          skillBindings: '技能绑定',
          promptBindings: '提示词绑定',
          mcpBindings: 'MCP 绑定',
          skillBindingControls: '技能绑定控制',
          mcpBindingControls: 'MCP 绑定控制',
          platformBindings: '平台绑定',
          availableArtifacts: '可用工件',
          defaultRuntime: '默认运行时',
        },
      },
      runtimeEvidence: {
        title: '运行时差异证据',
        description: '来自角色配置状态的已发布快照、实时引擎、重启和漂移证据。',
        snapshotDetail: '快照详情',
        sessionDetail: '会话详情',
        sessionJson: '会话 JSON',
        rows: {
          syncStatus: '同步状态',
          runtimeLoaded: '运行时已加载',
          liveApplied: '实时已应用',
          restartRequired: '需要重启',
          restartFields: '重启字段',
          runtimeEffect: '运行时影响',
          divergences: '差异',
        },
      },
    },
    resourceManagement: {
      tabs: {
        overview: '概览',
        redactedJson: '配置 JSON 视图',
      },
      table: {
        evidence: '证据',
        runtimeExposure: '运行时暴露',
        controls: '控制',
      },
      labels: {
        preset: '预设',
        source: '来源',
        actions: '操作',
        evidenceState: '证据状态',
        controlState: '控制状态',
      },
      actions: {
        skillLibrary: '技能库',
        loadedSkills: '运行时加载技能',
        createSkill: '新建技能',
        editDraft: '编辑草稿',
        lifecycleActions: '生命周期操作',
        openDetail: '打开详情',
        mcpServers: 'MCP 服务',
        mcpPresets: 'MCP 预设',
        toolProxy: '工具代理',
        createNative: '新建 Native',
        createProxy: '新建 Proxy',
        importPreset: '导入预设',
        openImportDrawer: '打开导入抽屉',
        editConfig: '编辑配置',
        editConnection: '编辑连接',
        promptPolicy: 'Prompt/策略',
        runtimeEvidence: '运行时证据',
        detailTabs: '详情标签',
        importDrawer: '导入抽屉',
        detail: '详情',
      },
      controls: {
        unavailableContractGap: '不可用：合约缺口',
        backendPreviewConfirmationGap: '不可用：后端预览+确认合约缺口',
        contractGapButton: '控制不可用：合约缺口',
        installContractGapMessage: '预设安装/导入可使用 secret registry 引用；MCP 生命周期和破坏性动作仍需预览+确认合约。',
        installPreviewConfirmationGap: '不可用：安装预览+确认合约缺口',
        presetInstallAvailable: '预设安装可用：POST /api/v1/mcp_presets/{name}/install 仅使用 secret registry 引用',
        presetInstallAction: '通过预设导入安装；不展示 secret 值',
        mutationPreviewConfirmationGap: '变更预览+确认合约缺口',
        enableDisableInstallPublishGap: '启用/禁用/安装/发布预览+确认合约缺口',
        personaBindingReadOnly: '角色绑定在此只读',
      },
      evidence: {
        runtimeNotInferred: '不推断',
        runtimeNotInferredStatus: '不推断',
        runtimeAssociationNotice: '角色/平台关联仅表示配置；运行时暴露需要实时技能/profile 证据。',
        loadedSkillsRuntimeOnly: '已加载技能仅使用实时角色详情证据；配置关联不视为运行时暴露。',
        requiresRuntimeProfileEvidence: '需要运行时/profile 证据，而不是角色/平台关联',
        configuredArtifactRuntimeUnknown: '已配置工件；运行时暴露在运行时/profile 证据存在前未知',
        mcpDefinitionRuntimeUnknown: '仅定义工件；运行时证据请查看 Tool Proxy',
      },
      presets: {
        summaryTitle: '预设摘要',
        registryTitle: '预设注册表',
        plannedDescription: '来自后端注册表的 MCP 预设。GitHub-style 导入保留快速创建路径，并且只使用 secret registry 引用。',
        emptyTitle: '未返回 MCP 预设。',
        missingDescription: '后端注册表未包含此预设。',
      },
    },
    systemSettings: {
      tabs: { overview: '概览', json: '查看配置 JSON' },
      nav: { settings: '设置', status: '系统状态', releaseOps: '发布运维', operationsDisabled: '运维操作已禁用', legacyHarness: '迁移兜底' },
      page: {
        eyebrow: '设置',
        loading: '正在加载实时管理 API 数据…',
        partialTitle: '部分/只读数据',
        partialSummary: '部分后端证据不完整。打开诊断可查看契约缺口、DTO/debug 细节和原始证据。',
        serviceBuild: '服务与构建',
        serviceBuildDescription: '来自实时状态和资产证据的可信字段。',
        configuredIntent: '配置意图',
        configuredIntentDescription: '配置意图与实时运行状态、部署产物状态分开呈现。',
        healthSummary: '健康摘要',
        healthSummaryDescription: '通过显式后端证据展示 API、存储、调度器、守护进程、平台适配器、中继和密钥注册健康。',
        deploymentFreshness: '部署新鲜度',
        deploymentFreshnessDescription: '当提交或资产元数据不完整时显示部分/陈旧证据。',
        releaseDeploymentDescription: '仅展示证据；不会把陈旧/部分状态断言为发布事实。',
        authTokenMode: '认证与令牌模式',
        authTokenModeDescription: '展示令牌模式、客户端持久化状态、退出能力和 401/403 行为。绝不渲染密钥值。',
        globalSettings: '仅全局设置',
        globalSettingsDescription: '全局 Console/System 设置放在这里。Persona、Prompt、Skill、MCP 配置属于 Persona 配置中心。',
        jsonRule: '全局 JSON 规则',
        jsonRuleDescription: 'System 仅保留一个配置 JSON 入口；编辑界面可使用 Draft JSON 右侧抽屉并展示 draft-vs-published diff。',
        sensitiveReveal: '敏感值查看保护',
        sensitiveRevealDescription: '除非后端对每次查看都要求重新输入令牌并写入脱敏审计证据，否则查看保持禁用。',
        sensitiveRevealGuard: '敏感值查看令牌重新输入保护',
        sensitiveRevealDrawerTitle: '敏感值查看令牌重新输入',
        sensitiveRevealEntryDescription: '敏感值查看只在此抽屉中提供，并且任何查看前都必须重新输入令牌。',
        reentryPlaceholder: '策略要求重新输入令牌',
        revealDisabled: '查看已禁用：契约缺口',
        auditEvidence: '审计证据',
        actionCenter: '明确可操作项',
        actionCenterDescription: '诊断和敏感值查看不会默认铺在页面上，需由操作员按需打开对应抽屉。',
        diagnosticsDetails: '诊断详情',
        diagnosticsDescription: '后端详细证据、DTO/debug 行和脱敏诊断 JSON 按需查看。',
        openDiagnostics: '诊断',
        openSensitiveReveal: '查看抽屉',
        closeDrawer: '关闭',
        runtimeStorage: '运行时存储',
        runtimeStorageDescription: '运行时计数为实时数据；存储/守护进程健康需要后端聚合证据。',
        schedulerDaemon: '调度器与守护进程',
        schedulerDaemonDescription: 'Cron 行为只读；除非后端报告，否则守护进程状态为部分证据。',
        platformAdapters: '平台适配器',
        platformAdaptersDescription: '来自状态和角色列表 DTO 的已连接/已配置平台证据。',
        platformFallback: '未返回实时平台证据',
        relaySecrets: '中继与密钥注册',
        relaySecretsDescription: '仅展示中继绑定和密钥注册计数；永不返回密钥材料。',
        openStructuredEditor: '编辑全局设置',
        globalSettingsEditorTitle: '结构化全局设置草稿',
        globalSettingsEditorDescription: '只用结构化控件编辑全局 System 设置。状态、诊断、部署新鲜度、运行时证据和敏感值查看不在此保存。',
        globalSettingsStructuredBoundary: '此草稿仅限 System 全局设置。Persona、Prompt、Skill、MCP 配置仍在各自区域管理，且不会渲染任何令牌或密钥值。',
        saveDraft: '保存草稿',
        discardDraft: '丢弃',
        openDraftJsonDiff: '草稿 JSON + diff',
        noUnsavedDraft: '无未保存草稿',
        unsavedDraft: '未保存草稿',
        structuredDraft: '结构化草稿',
        prefillSource: '预填来源',
        saveDraftLocalMessage: '结构化草稿已保存在当前控制台会话中；后端持久化仍与诊断、敏感查看和破坏性操作分离。',
        draftSaved: '草稿已保存',
        globalSettingsDraftJsonTitle: 'System 全局设置 Draft JSON + diff',
        backToStructuredEdit: '返回结构化编辑',
        globalSettingsDraftDiffTitle: 'Draft 与当前全局设置 diff',
        globalSettingsEmptyDiff: 'Draft 与当前全局设置没有差异。',
        managementApiIntentHelper: '管理 API 界面的布尔全局意图；实时可达性仍是只读证据。',
        webConsoleIntentHelper: 'Console v2 资产的布尔全局意图；部署新鲜度仍是只读证据。',
        schedulerIntentHelper: '调度器可用性的布尔全局意图；cron 行和守护进程健康保持诊断属性。',
        relayBridgeIntentHelper: '中继桥接可用性的布尔全局意图；中继健康和密钥注册计数保持只读。',
        relayBridgeCheckbox: '将中继桥接纳入全局设置草稿',
        generalSettings: '通用全局设置',
        generalSettingsDescription: '作用于整个服务的安全配置文件字段。',
        languageEnglish: '英文',
        languageChinese: '中文',
        cronSilentCheckbox: '抑制 cron 启动通知',
        displaySettings: '显示控制',
        displaySettingsDescription: '消息显示与 route-debug 控制。Persona 显示覆盖不在 System 中管理。',
        displayModeDefault: '默认',
        displayModeConcise: '简洁',
        displayModeQuiet: '安静',
        streamPreviewSettings: '流式预览控制',
        streamPreviewSettingsDescription: '安全的预览节奏控制；平台适配器凭据仍独立管理。',
        streamPreviewDisabledPlatformsHelper: '逗号分隔的平台名，例如 feishu, wecom。',
        limitsCleanupSettings: '频率限制与清理',
        limitsCleanupSettingsDescription: '全局频率限制和非活跃会话清理设置。',
        sessionCleanupCheckbox: '启用非活跃会话清理',
        legacyBridge: '旧版桥接',
        legacyBridgeDescription: '旧入口已退役；Console v2 是唯一正式前端。',
        cutoverChecklist: '切换检查清单',
        cutoverChecklistDescription: '发布就绪检查仅展示证据；契约缺口会明确标记。',
        actionGuards: '运维操作保护',
        actionGuardsDescription: '没有重启/重置/清空/回滚操作可一键执行；必须先有预览和确认。',
        action: '操作',
        check: '检查项',
        state: '状态',
        evidence: '证据',
        guardReason: '保护原因',
        control: '控件',
        backendDTO: '后端契约缺口',
        backendDTODescription: '用于权威呈现构建、部署、认证、存储、守护进程、敏感查看和操作事实。',
        diagnosticsDrawerTitle: '诊断详情',
        diagnosticNotices: '诊断提示',
        diagnosticNoticesDescription: '部分证据、后端契约缺口和 DTO 要求只在此抽屉中展示。',
        diagnosticEvidence: '运行时诊断证据',
        diagnosticEvidenceDescription: '只读运行时、调度器、平台、中继和密钥注册详情。',
        rawDiagnosticJson: '脱敏诊断 JSON',
        rawDiagnosticJsonDescription: '原始后端 DTO/debug 证据只在此抽屉中展示，并在渲染前脱敏。',
        releaseActionDrawerTitle: '操作预览与确认',
        previewStep: '预览',
        previewStepDescription: '危险操作必须先获取后端预览。缺少预览契约时操作保持不可用。',
        typedConfirmation: '输入确认',
        typedConfirmationDescription: '成功预览后仍需精确输入确认短语才能执行。',
        confirmationPhrase: '输入 {phrase} 以确认',
        operationUnavailable: '操作不可用',
        confirmOperation: '确认操作',
        openPreview: '打开预览',
        previewUnavailable: '预览不可用',
      },
      header: {
        statusTitle: '系统状态',
        statusDescription: '实时管理平面健康、部署证据、运行时状态、认证契约、中继和密钥注册可见性。部分事实会明确标记，不做推断。',
        releaseTitle: '发布运维',
        releaseDescription: '只读发布与运维保护状态。没有后端预览和显式确认时，破坏性操作保持禁用。',
        settingsTitle: '设置',
        settingsDescription: '全局设置、Console v2 认证状态、敏感值查看保护，以及唯一配置 JSON 规则。Persona/Prompt/Skill/MCP 配置有意排除。',
        kind: '类型',
        statusKind: '系统状态',
        releaseKind: '发布运维',
        settingsKind: '设置',
        source: '来源',
        mode: '模式',
        readOnly: '只读',
        guardedReadOnly: '受保护只读',
        structuredDraft: '结构化草稿',
      },
      status: { service: '服务', deployment: '部署', scheduler: '调度器', actions: '操作' },
      labels: {
        managementApiIntent: '管理 API 意图', webConsoleIntent: 'Web 控制台意图', schedulerIntent: '调度器意图', relayBridgeIntent: '中继桥接意图',
        version: '版本', commit: '提交', builtAt: '构建时间', uptime: '运行时长', webConsoleAssets: 'Web 控制台资产', managementApi: '管理 API', freshness: '新鲜度', artifactState: '产物状态', assetPath: '资产路径', assetCommit: '资产提交', assetFingerprint: '资产指纹', targetCommit: '目标提交', evidenceSource: '证据来源', backendAssetEvidence: '后端资产证据',
        managementToken: '管理令牌', serviceTokens: '服务令牌', tokenMode: '令牌模式', persistedToken: '持久化令牌状态', tokenStorage: '令牌存储键', logout: '退出登录', httpAuthErrors: '401/403 处理', secretRegistryEntries: '密钥注册条目', resolvedSecretRefs: '已解析密钥引用', missingSecretRefs: '缺失密钥引用', unsupportedSecretRefs: '不支持密钥引用', secretValues: '密钥值',
        globalScope: '设置范围', language: '语言', logLevel: '日志级别', cronSilent: 'Cron 静默模式', idleTimeoutMins: '空闲超时分钟', displayMode: '显示模式', displayThinkingMaxLen: '思考最大长度', displayToolMaxLen: '工具输出最大长度', displayRouteDebug: '路由调试', streamPreview: '流式预览', streamPreviewEnabled: '启用流式预览', streamPreviewIntervalMs: '预览间隔毫秒', streamPreviewMinDeltaChars: '预览最小增量字符', streamPreviewMaxChars: '预览最大字符数', streamPreviewDisabledPlatforms: '禁用预览的平台', rateLimit: '频率限制', rateLimitEnabled: '启用频率限制', rateLimitMaxMessages: '频率限制最大消息数', rateLimitWindowSecs: '频率限制窗口秒数', sessionCleanup: '会话清理', sessionCleanupEnabled: '启用会话清理', sessionCleanupInactiveDays: '清理非活跃天数', sessionCleanupKeepLatest: '清理保留最新数', prefillSource: '预填来源', draftPersistence: '草稿持久化', configPath: '配置路径', personaConfigBoundary: 'Persona 配置边界', viewConfigJsonEntrypoint: '配置 JSON 入口', draftJsonDrawer: 'Draft JSON 抽屉', draftPublishedDiff: 'Draft 与发布版本 diff', rawDebugTabs: '原始调试 JSON 标签页', previewContract: '预览契约',
        reenterToken: '重新输入管理令牌', revealSupport: '查看支持', revealContract: '查看契约', auditAction: '审计动作', auditEndpoint: '审计接口', auditRows: '查看审计行', latestAuditEvidence: '最近查看审计证据', auditSecretValues: '审计中的密钥值',
        personas: '角色', managementApiRuntime: '管理 API 运行态', liveSessions: '实时会话', runtimeProfiles: '运行时配置档', runtimeStorageHealth: '运行时存储健康', schedulerStatus: '调度器状态', totalJobs: '任务总数', enabledJobs: '已启用任务', daemonStatus: '守护进程状态', platformAdapters: '平台适配器', relayHealth: '中继健康', secretRegistryCounts: '密钥注册计数',
        legacyHarness: '迁移兜底', relayBindings: '中继绑定', legacyBridgeState: '旧版桥接状态', platforms: '平台',
      },
      states: {
        live: '实时', partialData: '部分数据', guarded: '已保护', reachable: '可访问', unavailable: '不可用', available: '可用', fresh: '最新', unknown: '未知', configured: '已配置', notConfigured: '未配置', statusCountOnly: '仅状态计数', neverRendered: '永不渲染', preservedHarness: '已作为迁移兜底保留', retiredLegacy: '已退役；使用 Console v2', relayReachable: '中继 API 可访问', needsBackendDTO: '需要后端聚合 DTO', partialModeUnavailable: '部分数据：模式不可用', partialStatusUnavailable: '部分数据：状态不可用', present: '存在', notPresent: '不存在', unknownPresence: '存在性未知', configuredCount: '已配置 {count} 个', enabledLimit: '已启用 · {count} 条消息 / {seconds} 秒', enabledCleanup: '已启用 · 非活跃 {days} 天 · 保留最新 {keep} 条', disabled: '已禁用', previewRequired: '需要预览', noLivePlatformEvidence: '未返回实时平台证据', contractGap: '契约缺口', required: '必需', notRequired: '非必需', requiredByPolicy: '策略要求', clientClearsPersistedToken: '客户端清除持久化令牌并返回登录', globalOnly: '仅全局设置', personaConfigSeparated: 'Persona/Prompt/Skill/MCP 配置不在 System 管理', singleEntrypoint: '仅一个脱敏配置 JSON 入口', draftDrawerOnly: '仅允许作为编辑态抽屉', diffRequiredWhenEditing: '显示 Draft JSON 时必须有 diff', notAllowed: '不允许', noAuditEvidence: '未返回查看审计证据',
      },
      actions: { restart: '重启服务', reset: '重置运行时状态', reload: '重载配置', flush: '清空运行时状态', rollback: '回滚部署', unknown: '未知操作' },
      checklist: {
        buildVersion: '版本与提交可见',
        frontendAssetFreshness: '前端资产新鲜度证据',
        authGuard: 'Console v2 令牌保护',
        legacyFrontendRetired: '旧前端已退役',
        sensitiveRevealAudit: '敏感值查看令牌重新输入与审计',
        releaseOperationPreview: '高风险操作预览与确认',
      },
      reasons: {
        guardUnavailable: '已禁用：后端预览和显式确认尚未端到端可用。',
        previewRequired: '执行前需要预览和显式确认。',
        backendPreviewUnavailable: '已禁用：后端预览不可用。',
        previewConfirmationUnavailable: '预览 + 确认不可用',
        backendAssetContractGap: '后端未暴露嵌入前端资产的提交/指纹。',
        sensitiveRevealContractGap: '后端尚未实现带令牌重新输入和脱敏审计证据的 reveal 接口。',
      },
      evidence: {
        statusEndpointUnavailable: '状态接口不可用',
        systemOverviewDeployment: 'GET /api/v1/system/overview 部署证据',
        assetCommitMatchesExpectedTarget: '资产提交与预期目标一致',
        assetCommitDiffersExpectedTarget: '资产提交与预期目标不一致',
        assetCommitBuildMetadataUnavailable: '资产提交/构建元数据不可用',
        webConsoleFalse: '/api/v1/status web_console=false',
        backendAggregateDTO: '后端聚合 DTO',
        authGuardClient: 'Console v2 登录包装器持久化令牌，退出会清除令牌，401/403 会强制重新认证',
      },
      notices: {
        separator: '：',
        detailSeparator: '；',
        deploymentFreshnessPrefix: '部署新鲜度',
        deploymentFreshnessSuffix: '需要后端聚合 DTO 提供权威构建/版本/提交状态',
        systemOverviewGap: '系统概览 DTO 不可用：需要 GET /api/v1/system/overview 才能区分剩余契约缺口和端点缺失',
        fallbackError: '部分证据不可用',
      },
    }
},
};

export const supportedLocales: Array<{ code: LocaleCode; label: string; shortLabel: string }> = [
  { code: 'en-US', label: 'English', shortLabel: 'EN' },
  { code: 'zh-CN', label: '简体中文', shortLabel: '中' },
];

export const resources: Record<LocaleCode, LocaleResource> = {
  'en-US': enUS,
  'zh-CN': zhCN,
};

export function t(key: I18nKey | string, locale: LocaleCode = 'en-US', values?: I18nValues) {
  const message = readResource(resources[locale], key) ?? readResource(resources['en-US'], key) ?? key;
  return interpolate(message, values);
}

export function detectLocale({
  storage = getBrowserStorage(),
  navigatorLanguage = getBrowserLanguage(),
}: {
  storage?: LocaleStorage | null;
  navigatorLanguage?: string;
} = {}): LocaleCode {
  const stored = storage?.getItem(localeStorageKey);
  if (isLocaleCode(stored)) return stored;
  return navigatorLanguage.toLowerCase().startsWith('zh') ? 'zh-CN' : 'en-US';
}

export function persistLocale(locale: LocaleCode, storage: LocaleStorage | null = getBrowserStorage()) {
  storage?.setItem(localeStorageKey, locale);
}

export function createI18nNamespace<Prefix extends string>(prefix: Prefix) {
  return {
    key<Suffix extends string>(suffix: Suffix) {
      return `${prefix}.${suffix}` as const;
    },
  };
}

export function isLocaleCode(value: unknown): value is LocaleCode {
  return value === 'en-US' || value === 'zh-CN';
}

function readResource(resource: LocaleResource, key: string) {
  const value = key.split('.').reduce<unknown>((currentValue, segment) => {
    if (currentValue && typeof currentValue === 'object' && segment in currentValue) {
      return (currentValue as Record<string, unknown>)[segment];
    }
    return undefined;
  }, resource);

  return typeof value === 'string' ? value : undefined;
}

function interpolate(message: string, values?: I18nValues) {
  if (!values) return message;
  return message.replace(/\{(\w+)\}/g, (match, key) => String(values[key] ?? match));
}

function getBrowserStorage() {
  return typeof localStorage === 'undefined' ? null : localStorage;
}

function getBrowserLanguage() {
  return typeof navigator === 'undefined' ? 'en-US' : navigator.language;
}
